import { useEffect, useMemo, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle,
  Award,
  CalendarDays,
  Clipboard,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Download,
  ImagePlus,
  Paperclip,
  RotateCcw,
  Save,
  Search,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react'
import { PedagogyBackLink } from '@/components/pedagogy/PedagogyBackLink'
import { registerUnsavedChangesGuard } from '@/lib/unsavedChanges'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import { prepareImageForUpload } from '@/lib/imageCompression'
import { SearchableSelect } from '@/components/pedagogy/SearchableSelect'
import { Button, Card, ProgressBar } from '@/components/ui'
import {
  COMMENT_FORMATS,
  applyCommentFormatToValue,
  renderFormattedComment,
  type CommentFormatDefinition,
} from '@/lib/commentFormatting'
import {
  TRAINER_CHECKLIST_OPTIONAL_CRITERION_IDS,
  TRAINER_CHECKLIST_SECTIONS,
  calculateTrainerChecklistMaxScore,
  calculateTrainerChecklistPercent,
  calculateTrainerChecklistScore,
  clampTrainerChecklistScore,
  createEmptyTrainerChecklistScores,
  createMaxTrainerChecklistScores,
  isOptionalTrainerChecklistCriterion,
} from '@/lib/trainerChecklist'
import {
  MIN_REVIEW_PHOTOS,
  TRAINER_REVIEW_MINUTE_OPTIONS,
  TRAINER_REVIEW_OTHER_DIRECTION_ID,
  clearTrainerChecklistDraft,
  createEmptyTrainerChecklistCriterionPhotos,
  composeTrainerReviewDateTime,
  createEmptyTrainerChecklistComments,
  createEmptyTrainerChecklistDraft,
  createTrainerChecklistDraftLink,
  createTrainerChecklistReviewLink,
  emptyTrainerChecklistMeta,
  formatTrainerReviewDate,
  hasTrainerChecklistDraft,
  normalizeTrainerChecklistDraft,
  openTrainerChecklistReport,
  readTrainerChecklistDraft,
  splitTrainerReviewDateTime,
  writeTrainerChecklistDraft,
  type TrainerChecklistCriterionPhotos,
  type TrainerChecklistMeta,
  type TrainerChecklistPhoto,
} from '@/lib/trainerChecklistReviews'
import {
  deleteMyTrainerReviewEntryPhoto,
  deleteTrainerReviewPhoto,
  ensureTrainerReviewDirection,
  getMyTrainerReviewEntryDetail,
  getTrainerReviewDetail,
  listTrainerReviewAvailableStudios,
  listTrainerReviewDirections,
  uploadMyTrainerReviewEntryPhoto,
  uploadTrainerReviewPhoto,
  upsertMyTrainerReviewEntry,
  upsertTrainerReview,
  type TrainerReviewDirectionOption,
} from '@/services/trainerReviewService'
import {
  createTeacherForPedagogyReviewAssignment,
  getMyPedagogyReviewAssignment,
  listMyPedagogyAssignmentTeacherOptions,
  listMyTrainerReviewEntryTeacherOptions,
  savePedagogyReviewAssignment,
  type PedagogyReviewAssignment,
} from '@/services/pedagogyReviewQueueService'
import {
  getTrainerReviewTeacherLink,
  listTrainerReviewTeacherOptions,
  type TrainerReviewTeacherOption,
} from '@/services/franchiseTeacherQualityService'
import {
  deleteSeniorTeacherReviewPhoto,
  getSeniorTeacherReviewContext,
  getSeniorTeacherReviewSessions,
  getSeniorTeacherManualReviewOptions,
  getSeniorTeacherReviewEditAccess,
  saveSeniorTeacherManualReview,
  saveSeniorTeacherReview,
  uploadSeniorTeacherReviewPhoto,
  type SeniorTeacherManualReviewOptions,
  type SeniorTeacherReviewContext,
  type SeniorTeacherReviewSession,
} from '@/services/seniorTeacherReviewService'
import { getPartnerTrainerReviewDetail } from '@/services/partnerTeachersService'
import type { Studio } from '@/types'

const UNSAVED_TRAINER_REVIEW_MESSAGE = 'Есть несохранённые изменения. Уйти без сохранения?'
const REQUIRED_PHOTOS_MESSAGE = `Добавьте минимум ${MIN_REVIEW_PHOTOS} общее фото перед сохранением.`
const TRAINER_REVIEW_TEACHER_DRAFT_KEY = 'trainer-review-teacher-draft-v1'

type PhotoTarget =
  | { kind: 'general' }
  | { kind: 'criterion'; criterionCode: string }

function friendlySeniorLoadError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '')
  const normalized = message.toLocaleLowerCase('ru-RU')
  if (normalized.includes('57014') || normalized.includes('statement timeout')) {
    return 'Не удалось быстро загрузить данные тренировки. Обновите страницу и попробуйте ещё раз.'
  }
  return message || 'Не удалось загрузить данные отсмотра'
}

function teacherStatusLabel(status: TrainerReviewTeacherOption['status']): string {
  if (status === 'working') return 'Работает'
  if (status === 'training') return 'В обучении'
  return 'Кандидат'
}

function formatPercent(value: number): string {
  return `${value.toLocaleString('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}

type CalendarTarget = 'training'
type ActiveCommentField = 'general' | `criterion:${string}` | null

const MAIN_TRAINING_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 9).padStart(2, '0'))
const ALL_TRAINING_HOURS = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'))

function getTodayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

function getDaysAgoIsoDate(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return date.toISOString().slice(0, 10)
}

function addCalendarMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1)
}

function getIsoDateFromCalendarDay(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function getCalendarMonthLabel(date: Date): string {
  return date.toLocaleDateString('ru-RU', {
    month: 'long',
    year: 'numeric',
  })
}

function getCalendarMonthFromIso(value: string): Date {
  const source = value || getTodayIsoDate()
  const match = source.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return new Date()
  return new Date(Number(match[1]), Number(match[2]) - 1, 1)
}

function buildStudioLabel(studio: Studio): string {
  const number = studio.studio_number ? `№${studio.studio_number}` : `ID ${studio.studio_internal_id}`
  const city = studio.city?.city_name?.trim() || 'Город не указан'
  const address = studio.address?.trim() || 'Адрес не указан'
  return `${number} · ${city} · ${address}`
}

function CalendarDateField({
  label,
  value,
  target,
  openTarget,
  calendarMonth,
  onOpen,
  onMonthChange,
  onChange,
}: {
  label: string
  value: string
  target: CalendarTarget
  openTarget: CalendarTarget | null
  calendarMonth: Date
  onOpen: (target: CalendarTarget, value: string) => void
  onMonthChange: (date: Date) => void
  onChange: (value: string) => void
}) {
  const isOpen = openTarget === target
  const year = calendarMonth.getFullYear()
  const month = calendarMonth.getMonth()
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const today = getTodayIsoDate()
  const cells = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ]

  return (
    <div data-date-time-picker="true" className="relative space-y-1.5 text-sm">
      {label ? <span className="font-medium text-muted-foreground">{label}</span> : null}
      <button
        type="button"
        onClick={() => onOpen(target, value)}
        className="input flex w-full items-center justify-between gap-2 text-left tabular-nums"
      >
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>
          {formatTrainerReviewDate(value) || 'ДД.ММ.ГГГГ'}
        </span>
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
      </button>
      {isOpen && (
        <div className="absolute left-0 top-full z-40 mt-1 w-72 rounded-card border border-border bg-card p-3 shadow-card">
          <div className="mb-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => onMonthChange(addCalendarMonths(calendarMonth, -1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-muted"
              aria-label="Предыдущий месяц"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="text-sm font-semibold capitalize">{getCalendarMonthLabel(calendarMonth)}</div>
            <button
              type="button"
              onClick={() => onMonthChange(addCalendarMonths(calendarMonth, 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-muted"
              aria-label="Следующий месяц"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase text-muted-foreground">
            {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
              <div key={day} className="py-1">{day}</div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {cells.map((day, index) => {
              if (!day) return <div key={`empty-${index}`} className="h-9" />

              const isoDate = getIsoDateFromCalendarDay(year, month, day)
              const isSelected = isoDate === value
              const isToday = isoDate === today

              return (
                <button
                  key={isoDate}
                  type="button"
                  onClick={() => onChange(isoDate)}
                  className={`h-9 rounded-lg text-sm tabular-nums transition-colors ${
                    isSelected
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : isToday
                        ? 'border border-primary/60 bg-primary/10 font-medium'
                        : 'hover:bg-muted'
                  }`}
                >
                  {day}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function TrainingTimePicker({
  hour,
  minute,
  showAllHours,
  highlightMinutes,
  onToggleAllHours,
  onHourSelect,
  onMinuteSelect,
}: {
  hour: string
  minute: string
  showAllHours: boolean
  highlightMinutes: boolean
  onToggleAllHours: () => void
  onHourSelect: (hour: string) => void
  onMinuteSelect: (minute: string) => void
}) {
  const hours = showAllHours ? ALL_TRAINING_HOURS : MAIN_TRAINING_HOURS

  return (
    <div className="rounded-lg border border-border bg-background p-2">
      <div className="grid grid-cols-4 gap-1 sm:grid-cols-5 xl:grid-cols-4">
        {hours.map(item => {
          const isSelected = item === hour

          return (
            <button
              key={item}
              type="button"
              onClick={() => onHourSelect(item)}
              className={`h-9 rounded-lg text-sm font-medium tabular-nums transition-colors ${
                isSelected
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card text-foreground hover:bg-muted'
              }`}
            >
              {item}:00
            </button>
          )
        })}
        <button
          type="button"
          onClick={onToggleAllHours}
          className="h-9 rounded-lg bg-muted px-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-border hover:text-foreground"
        >
          {showAllHours ? 'Скрыть' : 'Еще'}
        </button>
      </div>

      {showAllHours && (
        <div
          className={`mt-2 flex flex-wrap items-center gap-1.5 rounded-lg px-2 py-1.5 transition-colors duration-300 ${
            highlightMinutes ? 'bg-primary/20 ring-1 ring-primary/50' : 'bg-muted/60'
          }`}
        >
          <span className="mr-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Мин</span>
          {TRAINER_REVIEW_MINUTE_OPTIONS.map(item => {
            const isSelected = item === minute

            return (
              <button
                key={item}
                type="button"
                onClick={() => onMinuteSelect(item)}
                className={`h-7 min-w-10 rounded-md px-2 text-xs font-semibold tabular-nums transition-colors ${
                  isSelected
                    ? 'bg-secondary text-secondary-foreground'
                    : 'bg-card text-muted-foreground hover:bg-background hover:text-foreground'
                }`}
              >
                :{item}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TrainingTimeField({
  hour,
  minute,
  isOpen,
  showAllHours,
  highlightMinutes,
  onOpenChange,
  onToggleAllHours,
  onHourSelect,
  onMinuteSelect,
}: {
  hour: string
  minute: string
  isOpen: boolean
  showAllHours: boolean
  highlightMinutes: boolean
  onOpenChange: (isOpen: boolean) => void
  onToggleAllHours: () => void
  onHourSelect: (hour: string) => void
  onMinuteSelect: (minute: string) => void
}) {
  return (
    <div data-date-time-picker="true" className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!isOpen)}
        className="input flex w-full items-center justify-between gap-2 text-left tabular-nums"
      >
        <span>{hour}:{minute}</span>
        <Clock className="h-4 w-4 text-muted-foreground" />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full z-40 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-card border border-border bg-card p-2 shadow-card">
          <TrainingTimePicker
            hour={hour}
            minute={minute}
            showAllHours={showAllHours}
            highlightMinutes={highlightMinutes}
            onToggleAllHours={onToggleAllHours}
            onHourSelect={onHourSelect}
            onMinuteSelect={onMinuteSelect}
          />
        </div>
      )}
    </div>
  )
}

function ScoreButtonGroup({
  value,
  maxScore,
  label,
  onChange,
}: {
  value: number | null | undefined
  maxScore: number
  label: string
  onChange: (value: number | null) => void
}) {
  const options = maxScore === 1
    ? [0, 0.5, 1]
    : Array.from({ length: maxScore + 1 }, (_, index) => index)

  return (
    <div
      className="mx-auto grid justify-center gap-1.5"
      style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 6)}, 2rem)` }}
    >
      {options.map(option => {
        const isSelected = value === option
        const ratio = maxScore > 0 ? option / maxScore : 0
        const tone = ratio <= 0
          ? {
            idle: 'border-error/25 bg-error/10 text-error/80 hover:border-error/50 hover:bg-error/20',
            selected: 'border-error bg-error text-white shadow-sm',
          }
          : ratio < 0.5
            ? {
              idle: 'border-amber-400/35 bg-amber-100 text-amber-800 hover:border-amber-500 hover:bg-amber-200',
              selected: 'border-amber-500 bg-amber-500 text-white shadow-sm',
            }
            : ratio < 1
              ? {
                idle: 'border-primary/25 bg-primary/10 text-primary/80 hover:border-primary/45 hover:bg-primary/20',
                selected: 'border-primary bg-primary text-primary-foreground shadow-sm',
              }
              : {
                idle: 'border-success/25 bg-success/10 text-success hover:border-success/45 hover:bg-success/20',
                selected: 'border-success bg-success text-white shadow-sm',
              }

        return (
          <button
            key={option}
            type="button"
            aria-label={`Оценка ${option}: ${label}`}
            aria-pressed={isSelected}
            onClick={() => onChange(isSelected ? null : option)}
            className={`h-8 w-8 rounded-md border text-sm font-semibold tabular-nums transition-colors ${
              isSelected ? tone.selected : tone.idle
            }`}
          >
            {Number.isInteger(option) ? option : option.toLocaleString('ru-RU')}
          </button>
        )
      })}
    </div>
  )
}

function CommentFormatToolbar({
  disabled = false,
  onFormat,
}: {
  disabled?: boolean
  onFormat: (definition: CommentFormatDefinition) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {COMMENT_FORMATS.map(definition => (
        <button
          key={definition.type}
          type="button"
          title={definition.title}
          aria-label={definition.title}
          disabled={disabled}
          onMouseDown={event => event.preventDefault()}
          onClick={() => onFormat(definition)}
          className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md border border-border bg-background px-2 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 ${definition.className}`}
        >
          {definition.label}
        </button>
      ))}
    </div>
  )
}

function hasCommentFormattingSyntax(value: string): boolean {
  return value.includes('**') || value.includes('_') || value.includes('++')
}

function resizeCommentTextarea(textarea: HTMLTextAreaElement | null): void {
  if (!textarea) return

  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

function SearchableStudioPicker({
  studios,
  selectedStudioId,
  query,
  onQueryChange,
  onChange,
}: {
  studios: Studio[]
  selectedStudioId: string
  query: string
  onQueryChange: (value: string) => void
  onChange: (studio: Studio | null) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedStudio = studios.find(studio => String(studio.studio_internal_id) === selectedStudioId) ?? null
  const filteredStudios = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return studios

    return studios.filter(studio => {
      const haystack = [
        studio.studio_number,
        studio.city?.city_name,
        studio.address,
        studio.studio_internal_id,
      ]
        .map(value => String(value ?? '').toLowerCase())
        .join(' ')

      return haystack.includes(normalizedQuery)
    })
  }, [query, studios])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        onQueryChange('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onQueryChange])

  return (
    <div ref={dropdownRef} className="relative min-w-0">
      <button
        type="button"
        onClick={() => {
          setIsOpen(open => !open)
          if (isOpen) onQueryChange('')
        }}
        className="input flex w-full items-center justify-between gap-2 text-left"
      >
        <span className={selectedStudio ? 'truncate' : 'text-muted-foreground'}>
          {selectedStudio ? buildStudioLabel(selectedStudio) : 'Выберите студию'}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[320px] overflow-hidden rounded-lg border border-border bg-card shadow-lg">
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md bg-muted/50 px-2 py-2">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                value={query}
                onChange={event => onQueryChange(event.target.value)}
                placeholder="Поиск по номеру, городу или адресу"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filteredStudios.map(studio => {
              const selected = String(studio.studio_internal_id) === selectedStudioId
              return (
                <button
                  key={studio.studio_internal_id}
                  type="button"
                  onClick={() => {
                    onChange(studio)
                    onQueryChange('')
                    setIsOpen(false)
                  }}
                  className={`flex w-full items-start gap-2 px-3 py-2 text-left transition-colors ${
                    selected ? 'bg-primary/10' : 'hover:bg-muted/50'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate text-sm font-medium ${selected ? 'text-primary' : ''}`}>
                      №{studio.studio_number || studio.studio_internal_id}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {studio.city?.city_name || 'Город не указан'} · {studio.address || 'Адрес не указан'}
                    </span>
                  </span>
                </button>
              )
            })}
            {filteredStudios.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Ничего не найдено.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function PhotoTiles({
  photos,
  emptyMessage,
  compact = false,
  onPreview,
  onRemove,
}: {
  photos: TrainerChecklistPhoto[]
  emptyMessage: string
  compact?: boolean
  onPreview: (photo: TrainerChecklistPhoto) => void
  onRemove: (photo: TrainerChecklistPhoto) => void
}) {
  if (photos.length === 0) {
    if (compact) return null

    return (
      <div className={`rounded-lg border border-dashed border-border text-sm text-muted-foreground ${
        compact ? 'px-3 py-3' : 'px-4 py-6'
      }`}>
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className={compact ? 'flex flex-wrap gap-2' : 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4'}>
      {photos.map(photo => (
        <div
          key={photo.id}
          onClick={() => onPreview(photo)}
          className={`group relative overflow-hidden rounded-lg border border-border bg-muted ${
            compact ? 'h-24 w-24 cursor-zoom-in' : 'cursor-zoom-in'
          }`}
        >
          <img
            src={photo.previewUrl}
            alt={photo.name}
            className={compact ? 'h-full w-full object-cover' : 'aspect-[4/3] w-full object-cover'}
          />
          {compact ? (
            /* Белый здесь остаётся литералом: плашка лежит НА фотографии, а её
               содержимое от темы не зависит. `bg-card` в тёмной теме дал бы
               тёмную кнопку с красной иконкой поверх произвольного снимка. */
            <button
              type="button"
              onClick={event => {
                event.stopPropagation()
                onRemove(photo)
              }}
              className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-md bg-white/90 text-error shadow-sm transition-colors hover:bg-white"
              aria-label={`Удалить фото ${photo.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : (
            <>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent p-3 text-white">
                <div className="truncate text-sm font-medium">{photo.name}</div>
                <div className="mt-1 text-xs text-white/85">
                  {photo.file ? 'Фото добавлено и будет загружено при сохранении' : 'Фото сохранено'}
                </div>
              </div>
              {/* Белый — литерал по той же причине, что и у compact-варианта выше:
                  плашка лежит на фотографии, а не на поверхности приложения. */}
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation()
                  onRemove(photo)
                }}
                className="absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-md bg-white/90 text-error shadow-sm transition-colors hover:bg-white"
                aria-label={`Удалить фото ${photo.name}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
        </div>
      ))}
    </div>
  )
}

function PhotoPreviewOverlay({
  photo,
  onClose,
}: {
  photo: TrainerChecklistPhoto | null
  onClose: () => void
}) {
  useEffect(() => {
    if (!photo) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [photo, onClose])

  if (!photo) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      {/* Просмотрщик фото затемнён `bg-black/80` в обеих темах, поэтому белые
          `bg-white/10` и `text-white` здесь — правильный белый, а не пропуск. */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
        aria-label="Закрыть просмотр фото"
      >
        <X className="h-5 w-5" />
      </button>
      <div
        className="max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-card shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        <img
          src={photo.previewUrl}
          alt={photo.name}
          className="max-h-[90vh] max-w-[90vw] object-contain"
        />
      </div>
    </div>
  )
}

function InlinePhotoAttach({
  count,
  expanded,
  disabled = false,
  onToggle,
}: {
  count: number
  expanded: boolean
  disabled?: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${
        expanded
          ? 'border-primary bg-primary/10 text-primary'
          : disabled
            ? 'border-border bg-muted text-muted-foreground/60'
            : 'border-border bg-background text-muted-foreground hover:border-primary/35 hover:bg-primary/5 hover:text-foreground'
      }`}
      aria-label={count > 0 ? `Добавить фото, сейчас ${count}` : 'Добавить фото'}
    >
      <ImagePlus className="h-4 w-4" />
      {count > 0 && (
        <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-white">
          {count}
        </span>
      )}
    </button>
  )
}

function getConclusion(percent: number): string {
  if (percent >= 100) return 'Регламент соблюден полностью'
  if (percent < 50) return 'Тренеру необходимо записаться на пересдачу регламента'
  return 'Регламент выполнен частично'
}

function toUserFacingErrorMessage(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : String(error ?? '')
  const normalized = message.toLowerCase()

  if (
    normalized.includes('not valid json')
    || normalized.includes('unexpected token')
    || normalized.includes('not found')
    || normalized.includes('bucket')
    || normalized.includes('storage')
    || normalized.includes('row-level security')
    || normalized.includes('permission denied')
    || normalized.includes('access denied')
  ) {
    return fallback
  }

  return message.replace(/\s+/g, ' ').trim() || fallback
}

async function readPhotoFile(sourceFile: File): Promise<TrainerChecklistPhoto> {
  const { file, widthPx, heightPx } = await prepareImageForUpload(sourceFile)
  const previewUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
    reader.onerror = () => reject(reader.error ?? new Error(`Не удалось прочитать файл ${file.name}`))
    reader.readAsDataURL(file)
  })

  return {
    id: `${Date.now()}-${file.name}-${Math.random().toString(16).slice(2)}`,
    name: file.name,
    previewUrl,
    file,
    mimeType: file.type || undefined,
    sizeBytes: file.size,
    widthPx,
    heightPx,
  }
}

async function readClipboardImageFiles(dataTransfer: DataTransfer | null): Promise<File[]> {
  if (!dataTransfer) return []

  const imageFiles = Array.from(dataTransfer.items)
    .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item, index) => {
      const file = item.getAsFile()
      if (!file) return null

      if (file.name) return file

      const extension = file.type.split('/')[1] || 'png'
      return new File([file], `screenshot-${Date.now()}-${index}.${extension}`, { type: file.type || 'image/png' })
    })
    .filter((file): file is File => Boolean(file))

  return imageFiles
}

async function readClipboardImageFilesFromApi(): Promise<File[]> {
  if (typeof navigator === 'undefined' || !navigator.clipboard || typeof navigator.clipboard.read !== 'function') {
    throw new Error('Браузер не поддерживает чтение изображений из буфера обмена')
  }

  const items = await navigator.clipboard.read()
  const files: File[] = []

  for (const item of items) {
    const imageType = item.types.find(type => type.startsWith('image/'))
    if (!imageType) continue

    const blob = await item.getType(imageType)
    const extension = imageType.split('/')[1] || 'png'
    files.push(new File([blob], `clipboard-${Date.now()}-${files.length}.${extension}`, { type: imageType }))
  }

  return files
}

function countCriterionPhotos(criterionPhotos: TrainerChecklistCriterionPhotos): number {
  return Object.values(criterionPhotos).reduce((sum, photos) => sum + photos.length, 0)
}

function deriveHiddenCriterionRows(
  scores: Record<string, number | null>,
  hiddenCriterionRows: Record<string, boolean> = {}
): Record<string, boolean> {
  return Object.fromEntries(
    TRAINER_CHECKLIST_OPTIONAL_CRITERION_IDS
      .filter(itemId => hiddenCriterionRows[itemId] || scores[itemId] == null)
      .map(itemId => [itemId, true])
  )
}

function createTrainerChecklistDraftSignature(draft: {
  meta: TrainerChecklistMeta
  scores: Record<string, number | null>
  comments: Record<string, string>
  generalComment: string
  generalPhotos: TrainerChecklistPhoto[]
  criterionPhotos: TrainerChecklistCriterionPhotos
  hiddenCriterionRows: Record<string, boolean>
}): string {
  return JSON.stringify({
    meta: draft.meta,
    scores: draft.scores,
    comments: draft.comments,
    hiddenCriterionRows: draft.hiddenCriterionRows,
    generalComment: draft.generalComment,
    generalPhotos: draft.generalPhotos.map(photo => ({
      id: photo.id,
      name: photo.name,
      photoId: photo.photoId ?? null,
      photoKey: photo.photoKey ?? null,
      reviewScoreId: photo.reviewScoreId ?? null,
      storageBucket: photo.storageBucket ?? null,
      storagePath: photo.storagePath ?? null,
      hasLocalFile: Boolean(photo.file),
    })),
    criterionPhotos: Object.fromEntries(
      Object.entries(draft.criterionPhotos).map(([criterionCode, photos]) => [
        criterionCode,
        photos.map(photo => ({
          id: photo.id,
          name: photo.name,
          photoId: photo.photoId ?? null,
          photoKey: photo.photoKey ?? null,
          reviewScoreId: photo.reviewScoreId ?? null,
          storageBucket: photo.storageBucket ?? null,
          storagePath: photo.storagePath ?? null,
          hasLocalFile: Boolean(photo.file),
        })),
      ])
    ),
  })
}

export function TrainerChecklistPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { partnerNumber = '', studioId: seniorStudioIdParam = '' } = useParams<{
    partnerNumber?: string
    studioId?: string
  }>()
  const allowNextNavigationRef = useRef(false)
  const restoreLocationOnPopRef = useRef(false)
  const generalPhotoInputRef = useRef<HTMLInputElement | null>(null)
  const criterionPhotoInputRef = useRef<HTMLInputElement | null>(null)
  const generalCommentRef = useRef<HTMLTextAreaElement | null>(null)
  const criterionCommentRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const reviewId = searchParams.get('reviewId')
  const seniorReviewKeyParam = searchParams.get('reviewKey') || ''
  const isSeniorPartnerMode = Boolean(partnerNumber && seniorStudioIdParam)
  const seniorAnchorStudioId = Number(seniorStudioIdParam)
  const entryKey = searchParams.get('entryKey')
  const assignmentKey = searchParams.get('assignmentKey') || null
  const shouldOpenDraft = searchParams.get('draft') === '1'
  const returnTo = searchParams.get('returnTo') || ''
  const preselectedTeacherId = searchParams.get('teacherId') || ''

  const [activeReviewId, setActiveReviewId] = useState<string | null>(reviewId)
  const [activeSeniorReviewKey, setActiveSeniorReviewKey] = useState<string | null>(seniorReviewKeyParam || null)
  const [activeSeniorReviewId, setActiveSeniorReviewId] = useState<string | null>(null)
  const [seniorContext, setSeniorContext] = useState<SeniorTeacherReviewContext | null>(null)
  const [selectedSeniorCityId, setSelectedSeniorCityId] = useState<string>('')
  const [selectedSeniorSessionItemId, setSelectedSeniorSessionItemId] = useState<string>('')
  const [seniorSessions, setSeniorSessions] = useState<SeniorTeacherReviewSession[]>([])
  const [seniorSessionsLoading, setSeniorSessionsLoading] = useState(false)
  const [seniorSessionsError, setSeniorSessionsError] = useState('')
  const [seniorManualMode, setSeniorManualMode] = useState(false)
  const [seniorManualOptions, setSeniorManualOptions] = useState<SeniorTeacherManualReviewOptions | null>(null)
  const [seniorManualOptionsLoading, setSeniorManualOptionsLoading] = useState(false)
  const [seniorManualOptionsError, setSeniorManualOptionsError] = useState('')
  const [seniorLockedDirectionName, setSeniorLockedDirectionName] = useState<string>('')
  const [activeEntryKey, setActiveEntryKey] = useState<string | null>(entryKey)
  const [queueAssignment, setQueueAssignment] = useState<PedagogyReviewAssignment | null>(null)
  const [meta, setMeta] = useState<TrainerChecklistMeta>(emptyTrainerChecklistMeta)
  // Ссылка ведёт и на <label> (обычный отсмотр), и на <div> (форма старшего),
  // поэтому тип общий: failValidation принимает HTMLElement.
  const studioFieldRef = useRef<HTMLElement | null>(null)
  const setStudioFieldRef = (element: HTMLElement | null) => {
    studioFieldRef.current = element
  }
  const teacherFieldRef = useRef<HTMLDivElement | null>(null)
  const photosSectionRef = useRef<HTMLDivElement | null>(null)
  const criterionRowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const [scores, setScores] = useState(createEmptyTrainerChecklistScores())
  const [comments, setComments] = useState(createEmptyTrainerChecklistComments())
  const [generalComment, setGeneralComment] = useState('')
  const [generalPhotos, setGeneralPhotos] = useState<TrainerChecklistPhoto[]>([])
  const [criterionPhotos, setCriterionPhotos] = useState<TrainerChecklistCriterionPhotos>(createEmptyTrainerChecklistCriterionPhotos())
  const [expandedCriterionPhotoPanels, setExpandedCriterionPhotoPanels] = useState<Record<string, boolean>>({})
  const [hiddenCriterionRows, setHiddenCriterionRows] = useState<Record<string, boolean>>({})
  const [directionOptions, setDirectionOptions] = useState<TrainerReviewDirectionOption[]>([])
  const [teacherOptions, setTeacherOptions] = useState<TrainerReviewTeacherOption[]>([])
  const [selectedTeacherId, setSelectedTeacherId] = useState(preselectedTeacherId)
  const [savedTeacherId, setSavedTeacherId] = useState(preselectedTeacherId)
  const [teacherOptionsLoading, setTeacherOptionsLoading] = useState(false)
  const [teacherOptionsError, setTeacherOptionsError] = useState('')
  const [showCreateTeacher, setShowCreateTeacher] = useState(false)
  const [newTeacherName, setNewTeacherName] = useState('')
  const [creatingTeacher, setCreatingTeacher] = useState(false)
  const [createTeacherError, setCreateTeacherError] = useState('')
  const [availableStudios, setAvailableStudios] = useState<Studio[] | null>(null)
  const [pendingPhotoTarget, setPendingPhotoTarget] = useState<PhotoTarget>({ kind: 'general' })
  const [previewPhoto, setPreviewPhoto] = useState<TrainerChecklistPhoto | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState<string>('')
  const [saveWarning, setSaveWarning] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [studioQuery, setStudioQuery] = useState('')
  const [showDraftBanner, setShowDraftBanner] = useState(false)
  // Правка существующего отсмотра восстановлена из локального черновика:
  // на сервере её ещё нет, и человек обязан это видеть.
  const [recoveredReviewDraft, setRecoveredReviewDraft] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [openCalendar, setOpenCalendar] = useState<CalendarTarget | null>(null)
  const [calendarMonth, setCalendarMonth] = useState(() => getCalendarMonthFromIso(getTodayIsoDate()))
  const [isTrainingTimeOpen, setIsTrainingTimeOpen] = useState(false)
  const [showAllTrainingHours, setShowAllTrainingHours] = useState(false)
  const [highlightMinutes, setHighlightMinutes] = useState(false)
  const [activeCommentField, setActiveCommentField] = useState<ActiveCommentField>(null)
  const [previewCommentField, setPreviewCommentField] = useState<ActiveCommentField>(null)
  const [savedDraftSignature, setSavedDraftSignature] = useState(
    createTrainerChecklistDraftSignature(createEmptyTrainerChecklistDraft())
  )

  const studios = useMemo(() => availableStudios ?? [], [availableStudios])

  const selectedStudio = useMemo(
    () => studios.find(studio => String(studio.studio_internal_id) === meta.studioId) ?? null,
    [meta.studioId, studios]
  )

  const totalCriteriaCount = useMemo(
    () => TRAINER_CHECKLIST_SECTIONS.reduce((sum, section) => sum + section.items.length, 0),
    []
  )
  const requiredCriteriaCount = useMemo(
    () => totalCriteriaCount - TRAINER_CHECKLIST_OPTIONAL_CRITERION_IDS.length,
    [totalCriteriaCount]
  )
  const totalScore = useMemo(() => calculateTrainerChecklistScore(scores), [scores])
  const maxScore = useMemo(() => calculateTrainerChecklistMaxScore(scores), [scores])
  const qualityPercent = useMemo(() => calculateTrainerChecklistPercent(totalScore, maxScore), [maxScore, totalScore])
  const conclusion = useMemo(() => getConclusion(qualityPercent), [qualityPercent])
  const missingScore = maxScore - totalScore
  const unevaluatedItems = useMemo(
    () => TRAINER_CHECKLIST_SECTIONS
      .flatMap(section => section.items)
      .filter(item => !isOptionalTrainerChecklistCriterion(item.id) && !hiddenCriterionRows[item.id] && scores[item.id] == null),
    [hiddenCriterionRows, scores]
  )
  const unevaluatedCount = unevaluatedItems.length
  const evaluatedCount = requiredCriteriaCount - unevaluatedCount
  const trainingDateTimeParts = useMemo(
    () => splitTrainerReviewDateTime(meta.trainingDateTime),
    [meta.trainingDateTime]
  )
  const currentDraftSignature = useMemo(
    () => createTrainerChecklistDraftSignature({
      meta,
      scores,
      comments,
      generalComment,
      generalPhotos,
      criterionPhotos,
      hiddenCriterionRows,
    }),
    [comments, criterionPhotos, generalComment, generalPhotos, hiddenCriterionRows, meta, scores]
  )
  const activeRecordKey = isSeniorPartnerMode
    ? activeSeniorReviewKey
    : activeEntryKey ?? activeReviewId
  const activeDraftKey = isSeniorPartnerMode
    ? null
    : activeRecordKey ?? (assignmentKey ? `assignment-${assignmentKey}` : null)
  const isQueueBound = assignmentKey != null || activeEntryKey != null
  const isSeniorEditing = isSeniorPartnerMode && Boolean(activeSeniorReviewKey)
  const seniorSessionsInCity = useMemo(() => {
    if (!selectedSeniorCityId) return seniorSessions
    return seniorSessions.filter(session => String(session.cityId ?? '') === selectedSeniorCityId)
  }, [selectedSeniorCityId, seniorSessions])
  const seniorTeacherOptions = useMemo(() => {
    if (!seniorContext) return []
    if (!selectedSeniorCityId) return seniorContext.teachers
    const cityId = Number(selectedSeniorCityId)
    return seniorContext.teachers.filter(option => (option.cityIds ?? []).includes(cityId))
  }, [seniorContext, selectedSeniorCityId])
  const seniorSessionsForTeacher = useMemo(() => (
    selectedTeacherId
      ? seniorSessionsInCity.filter(session => session.teacherId === selectedTeacherId)
      : []
  ), [selectedTeacherId, seniorSessionsInCity])
  const selectedSeniorSession = useMemo(
    () => seniorSessionsForTeacher.find(session => session.itemId === selectedSeniorSessionItemId) ?? null,
    [selectedSeniorSessionItemId, seniorSessionsForTeacher]
  )
  const selectedSeniorTeacher = useMemo(
    () => seniorContext?.teachers.find(option => option.teacherId === selectedTeacherId) ?? null,
    [selectedTeacherId, seniorContext]
  )
  const seniorManualStudios = useMemo(() => {
    let studios = seniorManualOptions?.studios ?? []
    if (selectedSeniorCityId) {
      const cityId = Number(selectedSeniorCityId)
      studios = studios.filter(studio => studio.cityId === cityId)
    }
    if (!selectedSeniorTeacher || selectedSeniorTeacher.cityIds.length === 0) return studios
    return studios.filter(studio => studio.cityId == null || selectedSeniorTeacher.cityIds.includes(studio.cityId))
  }, [selectedSeniorCityId, selectedSeniorTeacher, seniorManualOptions?.studios])
  const isLegacyReviewWithoutTeacher = Boolean(activeRecordKey && !savedTeacherId)
  const selectedTeacherOption = useMemo(
    () => teacherOptions.find(option => option.teacherId === selectedTeacherId) ?? null,
    [selectedTeacherId, teacherOptions]
  )
  const queueTeacherLinkNotice = useMemo(() => {
    const sourceName = queueAssignment?.teacherNameRaw?.trim()
    if (!queueAssignment || !sourceName || !selectedTeacherOption) return null

    if (queueAssignment.resolvedTeacherId === selectedTeacherOption.teacherId) {
      return {
        kind: 'current' as const,
        text: `Имя «${sourceName}» уже связано с ${selectedTeacherOption.fullName}. Эта связь будет использоваться в следующих заданиях.`,
      }
    }

    if (queueAssignment.resolvedTeacherId) {
      return {
        kind: 'change' as const,
        text: `Вы меняете постоянную связь для «${sourceName}»: после сохранения следующие задания будут выбирать ${selectedTeacherOption.fullName}.`,
      }
    }

    return {
      kind: 'new' as const,
      text: `После сохранения «${sourceName}» будет постоянно связано с ${selectedTeacherOption.fullName} для этого Listok, франчайзи и города.`,
    }
  }, [queueAssignment, selectedTeacherOption])
  const hasUnsavedReviewChanges = currentDraftSignature !== savedDraftSignature
    || selectedTeacherId !== savedTeacherId
  const hasUnsavedChangesOnCurrentTab = hasUnsavedReviewChanges
  const canDownloadReport = Boolean(activeRecordKey)
    && !hasUnsavedReviewChanges
    && !saving
  const totalPhotoCount = useMemo(
    () => generalPhotos.length + countCriterionPhotos(criterionPhotos),
    [criterionPhotos, generalPhotos]
  )

  useEffect(() => {
    let cancelled = false

    async function loadOptionsAndReview() {
      setLoading(true)
      setError('')
      setSaveWarning('')
      setIsInitialized(false)

      try {
        if (isSeniorPartnerMode) {
          if (!Number.isInteger(seniorAnchorStudioId) || seniorAnchorStudioId <= 0) {
            throw new Error('Некорректная студия для senior-отсмотра')
          }

          const context = await getSeniorTeacherReviewContext(seniorAnchorStudioId)
          if (cancelled) return

          let nextDraft = createEmptyTrainerChecklistDraft()
          let nextTeacherId = ''
          let serverSignature: string | null = null
          let nextCityId = context.cities.length === 1 ? String(context.cities[0].cityId) : ''

          if (seniorReviewKeyParam) {
            const [editAccess, detail] = await Promise.all([
              getSeniorTeacherReviewEditAccess(seniorAnchorStudioId, seniorReviewKeyParam),
              getPartnerTrainerReviewDetail(seniorAnchorStudioId, seniorReviewKeyParam),
            ])
            if (!editAccess || !detail) {
              throw new Error('Этот отсмотр недоступен для редактирования')
            }

            nextTeacherId = editAccess.teacherId
            nextCityId = String(
              context.cities.find(city => city.cityName === detail.meta.city)?.cityId ?? ''
            )
            nextDraft = normalizeTrainerChecklistDraft({
              meta: {
                studioId: detail.meta.studioId,
                city: detail.meta.city,
                studioAddress: detail.meta.studioAddress,
                reviewDate: detail.meta.reviewDate,
                trainingDateTime: detail.meta.trainingDateTime,
                directionId: detail.meta.directionId,
                customDirectionName: '',
                coachName: detail.meta.coachName,
              },
              scores: detail.scores,
              comments: detail.comments,
              generalComment: detail.generalComment,
              generalPhotos: detail.generalPhotos,
              criterionPhotos: detail.criterionPhotos,
              hiddenCriterionRows: deriveHiddenCriterionRows(detail.scores),
            })
            serverSignature = createTrainerChecklistDraftSignature(nextDraft)
            setActiveSeniorReviewId(editAccess.reviewId)
            setActiveSeniorReviewKey(editAccess.reviewKey)
            setSeniorLockedDirectionName(detail.meta.directionName)
          } else {
            const hasPreselectedTeacher = preselectedTeacherId
              && context.teachers.some(option => option.teacherId === preselectedTeacherId)
            nextTeacherId = hasPreselectedTeacher ? preselectedTeacherId : ''
            const selectedOption = context.teachers.find(option => option.teacherId === nextTeacherId)
            nextDraft = normalizeTrainerChecklistDraft({
              ...createEmptyTrainerChecklistDraft(),
              meta: {
                ...emptyTrainerChecklistMeta,
                coachName: selectedOption?.fullName ?? '',
              },
            })
            serverSignature = createTrainerChecklistDraftSignature(nextDraft)
            setActiveSeniorReviewId(null)
            setActiveSeniorReviewKey(null)
            setSeniorLockedDirectionName('')
          }

          if (cancelled) return
          setSeniorContext(context)
          setSelectedSeniorCityId(nextCityId)
          setSelectedSeniorSessionItemId('')
          setSeniorSessions([])
          setSeniorSessionsError('')
          setSeniorManualMode(false)
          setSeniorManualOptions(null)
          setSeniorManualOptionsLoading(false)
          setSeniorManualOptionsError('')
          setDirectionOptions([])
          setAvailableStudios([])
          setTeacherOptions(context.teachers)
          setTeacherOptionsLoading(false)
          setTeacherOptionsError('')
          setActiveReviewId(null)
          setActiveEntryKey(null)
          setQueueAssignment(null)
          setSelectedTeacherId(nextTeacherId)
          setSavedTeacherId(nextTeacherId)
          setMeta(nextDraft.meta)
          setScores(nextDraft.scores)
          setComments(nextDraft.comments)
          setGeneralComment(nextDraft.generalComment)
          setGeneralPhotos(nextDraft.generalPhotos)
          setCriterionPhotos(nextDraft.criterionPhotos)
          setExpandedCriterionPhotoPanels({})
          setHiddenCriterionRows(deriveHiddenCriterionRows(nextDraft.scores, nextDraft.hiddenCriterionRows))
          setSavedDraftSignature(serverSignature ?? createTrainerChecklistDraftSignature(nextDraft))
          setShowDraftBanner(false)
          setRecoveredReviewDraft(false)
          setSaveStatus('')
          setSaveWarning('')
          setIsInitialized(true)
          return
        }

        const storedDraftExists = !reviewId && !entryKey && assignmentKey == null && !shouldOpenDraft && hasTrainerChecklistDraft()
        const [directions, initialScopedStudios, assignmentRow] = await Promise.all([
          listTrainerReviewDirections(),
          listTrainerReviewAvailableStudios(),
          assignmentKey == null ? Promise.resolve(null) : getMyPedagogyReviewAssignment(assignmentKey),
        ])

        const assigned = assignmentRow
        if (assignmentKey != null && (!assigned || !['open', 'in_progress'].includes(assigned.status))) {
          throw new Error('Задание не найдено или уже закрыто')
        }
        const scopedStudios = assigned && !initialScopedStudios.some(
          studio => studio.studio_internal_id === assigned.studioInternalId
        )
          ? [...initialScopedStudios, {
              studio_internal_id: assigned.studioInternalId,
              studio_number: assigned.studioNumber,
              city_id: 0,
              address: assigned.studioAddress,
              created_at: '',
              updated_at: '',
              city: {
                city_id: 0,
                city_name: assigned.cityName,
                population_thousands: null,
                city_population_group: null,
                city_land_group: null,
                country: '',
              },
            } satisfies Studio]
          : initialScopedStudios

        if (cancelled) return
        setDirectionOptions(directions)
        setAvailableStudios(scopedStudios)

        let nextDraft = createEmptyTrainerChecklistDraft()
        // Подпись сохранённого на сервере состояния и признак восстановления
        // черновика правки — нужны ниже, при выставлении savedDraftSignature.
        let serverSignature: string | null = null
        let restoredReviewDraft = false
        // Очередное задание никогда не наследует педагога из общей ручной формы:
        // один старый localStorage-слот иначе мог тихо привязать новый отсмотр
        // к педагогу из предыдущей студии.
        let nextTeacherId = assignmentKey == null
          ? preselectedTeacherId || (
              !reviewId && !entryKey
                ? localStorage.getItem(TRAINER_REVIEW_TEACHER_DRAFT_KEY) || ''
                : ''
            )
          : ''
        let linkedTeacher: TrainerReviewTeacherOption | null = null

        if (reviewId) {
          const [detail, teacherLink] = await Promise.all([
            getTrainerReviewDetail(reviewId),
            getTrainerReviewTeacherLink(reviewId),
          ])
          if (!detail) {
            throw new Error('Отсмотр не найден')
          }
          linkedTeacher = teacherLink
          nextTeacherId = teacherLink?.teacherId ?? ''

          nextDraft = normalizeTrainerChecklistDraft({
            meta: {
              ...detail.meta,
              customDirectionName: '',
            },
            scores: detail.scores,
            comments: detail.comments,
            generalComment: detail.generalComment,
            generalPhotos: detail.generalPhotos,
            criterionPhotos: detail.criterionPhotos,
            hiddenCriterionRows: deriveHiddenCriterionRows(detail.scores),
          })

          /*
            Незавершённая правка этого отсмотра. Подпись сохранённого состояния
            считаем ДО подмены — она и остаётся эталоном «что лежит на сервере»,
            поэтому восстановленная правка сразу считается несохранённой и её
            видит и кнопка «Сохранить», и перехват ухода.

            Сравнение с эталоном обязательно: черновик, совпадающий с сервером,
            это не правка, а след прошлого визита, и восстанавливать его молча —
            значит показывать плашку там, где ничего не терялось.
          */
          serverSignature = createTrainerChecklistDraftSignature(nextDraft)
          const storedReviewDraft = hasTrainerChecklistDraft(reviewId)
            ? readTrainerChecklistDraft(reviewId)
            : null
          if (
            storedReviewDraft
            && createTrainerChecklistDraftSignature(storedReviewDraft) !== serverSignature
          ) {
            nextDraft = storedReviewDraft
            restoredReviewDraft = true
          } else if (storedReviewDraft) {
            clearTrainerChecklistDraft(reviewId)
          }
        } else if (entryKey) {
          const detail = await getMyTrainerReviewEntryDetail(entryKey)
          if (!detail) throw new Error('Отсмотр не найден')
          nextTeacherId = detail.teacherId ?? ''
          linkedTeacher = detail.teacherId ? {
            teacherId: detail.teacherId,
            fullName: detail.meta.coachName,
            status: 'working',
            partnerNumbers: [],
            citiesText: detail.meta.city,
          } : null
          nextDraft = normalizeTrainerChecklistDraft({
            meta: { ...detail.meta, customDirectionName: '' },
            scores: detail.scores,
            comments: detail.comments,
            generalComment: detail.generalComment,
            generalPhotos: detail.generalPhotos,
            criterionPhotos: detail.criterionPhotos,
            hiddenCriterionRows: deriveHiddenCriterionRows(detail.scores),
          })
          serverSignature = createTrainerChecklistDraftSignature(nextDraft)
        } else if (assigned) {
          if (
            assigned.resolvedTeacherId
            && assigned.resolvedTeacherName
            && (assigned.resolvedTeacherStatus === 'candidate' || assigned.resolvedTeacherStatus === 'training' || assigned.resolvedTeacherStatus === 'working')
          ) {
            nextTeacherId = assigned.resolvedTeacherId
            linkedTeacher = {
              teacherId: assigned.resolvedTeacherId,
              fullName: assigned.resolvedTeacherName,
              status: assigned.resolvedTeacherStatus,
              partnerNumbers: [],
              citiesText: assigned.cityName,
            }
          }
          const direction = directions.find(option =>
            option.name.trim().toLocaleLowerCase('ru') === assigned.directionNameRaw?.trim().toLocaleLowerCase('ru')
          )
          nextDraft = normalizeTrainerChecklistDraft({
            ...createEmptyTrainerChecklistDraft(),
            meta: {
              ...emptyTrainerChecklistMeta,
              studioId: String(assigned.studioInternalId),
              city: assigned.cityName,
              studioAddress: assigned.studioAddress,
              trainingDateTime: `${assigned.trainingDate}T${assigned.trainingTime.slice(0, 5)}`,
              directionId: direction?.directionId
                ?? (assigned.directionNameRaw ? TRAINER_REVIEW_OTHER_DIRECTION_ID : ''),
              customDirectionName: direction ? '' : assigned.directionNameRaw ?? '',
              coachName: assigned.teacherNameRaw ?? '',
            },
          })
        } else if (shouldOpenDraft) {
          nextDraft = readTrainerChecklistDraft()
        }

        if (cancelled) return

        setActiveReviewId(reviewId)
        setActiveEntryKey(entryKey)
        setQueueAssignment(assigned)
        setShowCreateTeacher(false)
        setNewTeacherName(assigned?.teacherNameRaw?.trim() ?? '')
        setCreateTeacherError('')
        setSelectedTeacherId(nextTeacherId)
        setSavedTeacherId(nextTeacherId)
        if (linkedTeacher) {
          setTeacherOptions(current => current.some(item => item.teacherId === linkedTeacher?.teacherId)
            ? current
            : [...current, linkedTeacher as TrainerReviewTeacherOption].sort((a, b) => a.fullName.localeCompare(b.fullName, 'ru')))
        }
        setMeta(nextDraft.meta)
        setScores(nextDraft.scores)
        setComments(nextDraft.comments)
        setGeneralComment(nextDraft.generalComment)
        setGeneralPhotos(nextDraft.generalPhotos)
        setCriterionPhotos(nextDraft.criterionPhotos)
        setExpandedCriterionPhotoPanels({})
        setHiddenCriterionRows(deriveHiddenCriterionRows(nextDraft.scores, nextDraft.hiddenCriterionRows))
        setCalendarMonth(getCalendarMonthFromIso(
          splitTrainerReviewDateTime(nextDraft.meta.trainingDateTime).date || getTodayIsoDate()
        ))
        setOpenCalendar(null)
        setIsTrainingTimeOpen(false)
        setShowAllTrainingHours(false)
        setHighlightMinutes(false)
        setSavedDraftSignature(serverSignature ?? createTrainerChecklistDraftSignature(nextDraft))
        setShowDraftBanner(storedDraftExists)
        setRecoveredReviewDraft(restoredReviewDraft)
        setSaveStatus('')
        setSaveWarning('')
        setIsInitialized(true)
      } catch (loadError) {
        if (cancelled) return
        console.error('Failed to load trainer checklist:', loadError)
        setError(isSeniorPartnerMode ? friendlySeniorLoadError(loadError) : (loadError instanceof Error ? loadError.message : 'Не удалось загрузить отсмотр'))
        setIsInitialized(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadOptionsAndReview()

    return () => {
      cancelled = true
    }
  }, [
    assignmentKey,
    entryKey,
    isSeniorPartnerMode,
    navigate,
    preselectedTeacherId,
    reviewId,
    seniorAnchorStudioId,
    seniorReviewKeyParam,
    shouldOpenDraft,
  ])

  useEffect(() => {
    if (!isInitialized || !isSeniorPartnerMode || isSeniorEditing || seniorManualMode) return

    if (!selectedTeacherId) {
      setSeniorSessions([])
      setSeniorSessionsLoading(false)
      setSeniorSessionsError('')
      return
    }

    let cancelled = false
    setSeniorSessionsLoading(true)
    setSeniorSessionsError('')
    setSeniorSessions([])

    void getSeniorTeacherReviewSessions(seniorAnchorStudioId, selectedTeacherId)
      .then(sessions => {
        if (cancelled) return
        setSeniorSessions(sessions)
      })
      .catch(loadError => {
        if (cancelled) return
        setSeniorSessionsError(
          friendlySeniorLoadError(loadError)
        )
      })
      .finally(() => {
        if (!cancelled) setSeniorSessionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    isInitialized,
    isSeniorEditing,
    isSeniorPartnerMode,
    selectedTeacherId,
    seniorAnchorStudioId,
    seniorManualMode,
  ])

  useEffect(() => {
    if (!isInitialized) return
    if (isSeniorPartnerMode) {
      setTeacherOptionsLoading(false)
      setTeacherOptionsError('')
      return
    }

    let cancelled = false
    setTeacherOptionsLoading(true)
    setTeacherOptionsError('')

    if (!meta.studioId) {
      setTeacherOptions([])
      setTeacherOptionsLoading(false)
      setTeacherOptionsError('')
      return
    }

    // Сохранённая строка адресуется только непрозрачным ключом, задание из
    // очереди — ключом задания, обычный отсмотр — студией. Форма во всех трёх
    // случаях получает один и тот же список: разный набор педагогов сам по себе
    // был бы различием между видами результата.
    const teacherOptionsRequest = activeEntryKey
      ? listMyTrainerReviewEntryTeacherOptions(activeEntryKey)
      : queueAssignment
        ? listMyPedagogyAssignmentTeacherOptions(queueAssignment.assignmentKey)
        : listTrainerReviewTeacherOptions(Number(meta.studioId))

    teacherOptionsRequest
      .then(options => {
        if (cancelled) return
        const selectedFromResponse = options.find(item => item.teacherId === selectedTeacherId)
        if (selectedFromResponse && !meta.coachName.trim()) {
          setMeta(current => ({ ...current, coachName: selectedFromResponse.fullName }))
        }

        setTeacherOptions(current => {
          const selected = current.find(item => item.teacherId === selectedTeacherId)
          if (selected && !options.some(item => item.teacherId === selected.teacherId) && activeRecordKey) {
            return [...options, selected].sort((a, b) => a.fullName.localeCompare(b.fullName, 'ru'))
          }
          return options
        })

        if (selectedTeacherId && !selectedFromResponse && !activeRecordKey) {
          setSelectedTeacherId('')
        }
      })
      .catch(loadError => {
        if (cancelled) return
        console.error('Failed to load review teacher options:', loadError)
        setTeacherOptionsError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить педагогов')
      })
      .finally(() => {
        if (!cancelled) setTeacherOptionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeEntryKey, activeRecordKey, isInitialized, isSeniorPartnerMode, meta.coachName, meta.studioId, queueAssignment, selectedTeacherId])

  useEffect(() => {
    if (hasUnsavedChangesOnCurrentTab) {
      allowNextNavigationRef.current = false
    }
  }, [hasUnsavedChangesOnCurrentTab])

  useEffect(() => {
    if (hasUnsavedReviewChanges) {
      setSaveStatus('')
    }
  }, [hasUnsavedReviewChanges])

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChangesOnCurrentTab || allowNextNavigationRef.current) return

      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChangesOnCurrentTab])

  useEffect(() => {
    window.history.replaceState({ ...(window.history.state ?? {}), trainerChecklistGuard: true }, '')

    const handlePopState = () => {
      if (allowNextNavigationRef.current) {
        allowNextNavigationRef.current = false
        return
      }

      if (restoreLocationOnPopRef.current) {
        restoreLocationOnPopRef.current = false
        return
      }

      if (!hasUnsavedChangesOnCurrentTab || window.confirm(UNSAVED_TRAINER_REVIEW_MESSAGE)) {
        allowNextNavigationRef.current = true
        return
      }

      restoreLocationOnPopRef.current = true
      window.history.pushState(
        { ...(window.history.state ?? {}), trainerChecklistGuard: true },
        '',
        `${location.pathname}${location.search}${location.hash}`
      )
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [hasUnsavedChangesOnCurrentTab, location.hash, location.pathname, location.search])

  /*
    Ссылок `<a href>` во всём UI три штуки, поэтому прежний перехватчик кликов
    по ним закрывал ничтожную долю выходов и создавал ложное ощущение защиты.
    Его работу делает `registerUnsavedChangesGuard` ниже — его спрашивает вся
    навигация приложения. Здесь остаются только те выходы, до которых
    реестру не дотянуться: перезагрузка вкладки (beforeunload выше) и
    аппаратная кнопка «назад» (popstate выше).
  */

  useEffect(() => {
    if (!highlightMinutes) return
    const timeoutId = window.setTimeout(() => setHighlightMinutes(false), 1000)
    return () => window.clearTimeout(timeoutId)
  }, [highlightMinutes])

  useEffect(() => {
    if (!openCalendar && !isTrainingTimeOpen) return

    const handlePointerDown = (event: PointerEvent) => {
      if ((event.target as Element | null)?.closest('[data-date-time-picker="true"]')) return
      setOpenCalendar(null)
      setIsTrainingTimeOpen(false)
      setShowAllTrainingHours(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpenCalendar(null)
      setIsTrainingTimeOpen(false)
      setShowAllTrainingHours(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isTrainingTimeOpen, openCalendar])

  useEffect(() => {
    if (!selectedStudio) return

    setMeta(prev => ({
      ...prev,
      city: selectedStudio.city?.city_name ?? prev.city,
      studioAddress: selectedStudio.address ?? prev.studioAddress,
    }))
  }, [selectedStudio])

  /*
    Автосейв черновика. До 21.08.2026 он выключался при `activeReviewId`
    (`if (activeReviewId) return`), то есть ПРАВКА существующего отсмотра не
    была защищена ничем: ни черновиком, ни — до этой же правки — перехватом
    ухода. Теперь правка пишется в собственный слот `…-review-<id>`, чтобы не
    затирать начатый и не сохранённый новый отсмотр.

    Черновик правки пишется только при реальных отличиях от сохранённого
    состояния: иначе простое открытие карточки оставляло бы «несохранённые
    правки», и баннер восстановления показывался бы после каждого визита.
  */
  useEffect(() => {
    if (!isInitialized || shouldOpenDraft || showDraftBanner) return
    if (isSeniorPartnerMode) return
    if (activeDraftKey && !hasUnsavedReviewChanges) {
      clearTrainerChecklistDraft(activeDraftKey)
      return
    }

    writeTrainerChecklistDraft(normalizeTrainerChecklistDraft({
      meta,
      scores,
      comments,
      generalComment,
      generalPhotos,
      criterionPhotos,
      hiddenCriterionRows,
    }), activeDraftKey)

    // Педагог запоминается только для нового отсмотра: у существующего он
    // приезжает из его же связи и подстановка из чужого черновика была бы порчей.
    if (activeDraftKey) return
    if (selectedTeacherId) {
      localStorage.setItem(TRAINER_REVIEW_TEACHER_DRAFT_KEY, selectedTeacherId)
    } else {
      localStorage.removeItem(TRAINER_REVIEW_TEACHER_DRAFT_KEY)
    }
  }, [activeDraftKey, hasUnsavedReviewChanges, comments, criterionPhotos, generalComment, generalPhotos, hiddenCriterionRows, isInitialized, isSeniorPartnerMode, meta, scores, selectedTeacherId, shouldOpenDraft, showDraftBanner])

  const updateMeta = (key: keyof TrainerChecklistMeta, value: string) => {
    setShowDraftBanner(false)
    setSaveWarning('')
    setMeta(prev => ({ ...prev, [key]: value }))
  }

  const confirmLeaveWithUnsavedChanges = () => {
    if (!hasUnsavedChangesOnCurrentTab) return true
    return window.confirm(UNSAVED_TRAINER_REVIEW_MESSAGE)
  }

  const navigateWithUnsavedConfirmation = (path: string) => {
    if (!confirmLeaveWithUnsavedChanges()) return
    allowNextNavigationRef.current = true
    navigate(path)
  }

  /*
    Перехват ухода со страницы. Регистрируется в общем реестре приложения,
    поэтому его спрашивают ВСЕ точки входа в навигацию — нижняя навигация УК,
    навигация владельца, Header, селектор студии и вкладки раздела, — а не
    только вкладки, как было до 21.08.2026. Нижняя навигация УК это самый
    крупный кликабельный элемент экрана, и промах по ней уносил 8–15 минут
    заполнения без единого вопроса.

    Эффект без списка зависимостей — намеренно: колбэк замыкается на
    `hasUnsavedChangesOnCurrentTab` текущего рендера, поэтому перерегистрируется
    каждый раз. Реестр хранит функции по ссылке, cleanup снимает именно свою,
    и React выполняет его ДО регистрации новой — задвоения не возникает.
  */
  useEffect(() => registerUnsavedChangesGuard(() => {
    if (!confirmLeaveWithUnsavedChanges()) return false
    allowNextNavigationRef.current = true
    return true
  }))

  const openDateCalendar = (target: CalendarTarget, value: string) => {
    setOpenCalendar(current => current === target ? null : target)
    setIsTrainingTimeOpen(false)
    setShowAllTrainingHours(false)
    setCalendarMonth(getCalendarMonthFromIso(value))
  }

  const updateTrainingDate = (value: string) => {
    updateMeta('trainingDateTime', composeTrainerReviewDateTime(value, trainingDateTimeParts.hour, trainingDateTimeParts.minute))
    setOpenCalendar(null)
  }

  const updateTrainingTime = (hour: string, minute: string) => {
    const date = trainingDateTimeParts.date || getTodayIsoDate()
    updateMeta('trainingDateTime', composeTrainerReviewDateTime(date, hour, minute))
  }

  const selectTrainingHour = (hour: string) => {
    updateTrainingTime(hour, '00')
    if (showAllTrainingHours) setHighlightMinutes(true)
  }

  const selectTrainingMinute = (minute: string) => {
    updateTrainingTime(trainingDateTimeParts.hour || '09', minute)
    setHighlightMinutes(false)
    setIsTrainingTimeOpen(false)
    setShowAllTrainingHours(false)
  }

  /**
   * Все проверки формы срабатывают только на submit и пишут в один `error`,
   * отрисованный после шести таблиц и блока фото — на 4 000 пикселей ниже
   * верхней кнопки «Сохранить». Самый частый сценарий (забыл фото / пропустил
   * критерий) выглядел как «кнопка сломана», поэтому отказ теперь скроллит
   * к виновному контролу и называет критерии поимённо.
   */
  const scrollToValidationTarget = (element: HTMLElement | null) => {
    if (!element) return
    element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const focusable = element.matches('input, select, textarea, button')
      ? element
      : element.querySelector<HTMLElement>('input, select, textarea, button')
    window.setTimeout(() => focusable?.focus({ preventScroll: true }), 250)
  }

  const failValidation = (message: string, element: HTMLElement | null) => {
    setError(message)
    setSaveStatus('')
    setSaveWarning('')
    scrollToValidationTarget(element)
  }

  const updateScore = (itemId: string, value: unknown) => {
    setShowDraftBanner(false)
    setSaveWarning('')
    const nextScore = value == null ? null : clampTrainerChecklistScore(itemId, value)
    setScores(prev => ({
      ...prev,
      [itemId]: nextScore,
    }))
    if (nextScore != null) {
      setHiddenCriterionRows(prev => {
        if (!prev[itemId]) return prev
        return { ...prev, [itemId]: false }
      })
    }
  }

  /**
   * 24 обязательных клика по оценкам на каждый отсмотр (при 20–50 отсмотрах в
   * неделю — 500–1200 кликов только по баллам). Типовой ход отсмотра — «всё
   * максимум, кроме нескольких пунктов», поэтому кнопка проставляет максимум и
   * дальше оператор снижает нужные критерии.
   *
   * Действие НЕ разрушающее: уже выставленные оценки не перетираются, кнопка
   * заполняет только непроставленные. Подпись меняется вместе с поведением,
   * чтобы обещание кнопки совпадало с тем, что она сделает.
   */
  const fillRemainingWithMaxScores = () => {
    setShowDraftBanner(false)
    setSaveWarning('')
    const maxScores = createMaxTrainerChecklistScores()
    setScores(prev => {
      const next = { ...prev }
      for (const [itemId, maxScore] of Object.entries(maxScores)) {
        // Пропущенный оператором пункт остаётся пропущенным.
        if (hiddenCriterionRows[itemId]) continue
        if (next[itemId] == null) next[itemId] = maxScore
      }
      return next
    })
  }

  const toggleHiddenCriterionRow = (itemId: string) => {
    setShowDraftBanner(false)
    setSaveWarning('')
    setHiddenCriterionRows(prev => ({ ...prev, [itemId]: !prev[itemId] }))
    setScores(prev => ({
      ...prev,
      [itemId]: null,
    }))
  }

  const updateComment = (itemId: string, value: string) => {
    setShowDraftBanner(false)
    setSaveWarning('')
    setComments(prev => ({ ...prev, [itemId]: value }))
  }

  const updateGeneralComment = (value: string) => {
    setShowDraftBanner(false)
    setSaveWarning('')
    setGeneralComment(value)
  }

  const focusCriterionCommentField = (itemId: string) => {
    setActiveCommentField(`criterion:${itemId}`)
    setPreviewCommentField(prev => (prev === `criterion:${itemId}` ? prev : null))
  }

  const focusGeneralCommentField = () => {
    setActiveCommentField('general')
    setPreviewCommentField(prev => (prev === 'general' ? prev : null))
  }

  const applyFormatToTextarea = (
    textarea: HTMLTextAreaElement | null,
    currentValue: string,
    onChange: (value: string) => void,
    definition: CommentFormatDefinition
  ) => {
    if (!textarea) return

    const nextState = applyCommentFormatToValue(
      currentValue,
      textarea.selectionStart ?? currentValue.length,
      textarea.selectionEnd ?? currentValue.length,
      definition
    )

    onChange(nextState.value)

    window.requestAnimationFrame(() => {
      resizeCommentTextarea(textarea)
      textarea.focus()
      textarea.setSelectionRange(nextState.selectionStart, nextState.selectionEnd)
    })
  }

  const addPhotosToTarget = async (files: Iterable<File>, target: PhotoTarget) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'))
    if (imageFiles.length === 0) return

    try {
      setShowDraftBanner(false)
      setError('')
      const nextPhotos = await Promise.all(imageFiles.map(readPhotoFile))
      if (target.kind === 'general') {
        setGeneralPhotos(prev => [...prev, ...nextPhotos])
      } else {
        setExpandedCriterionPhotoPanels(prev => ({ ...prev, [target.criterionCode]: true }))
        setCriterionPhotos(prev => ({
          ...prev,
          [target.criterionCode]: [...(prev[target.criterionCode] ?? []), ...nextPhotos],
        }))
      }
      setSaveStatus('')
      setSaveWarning('')
    } catch (photoError) {
      console.error('Failed to read photos:', photoError)
      setError(toUserFacingErrorMessage(photoError, 'Не удалось добавить фото'))
    }
  }

  const addPhotos = async (files: FileList | null, target: PhotoTarget) => {
    if (!files?.length) return
    await addPhotosToTarget(Array.from(files), target)
  }

  const addPhotosFromClipboard = async (target: PhotoTarget) => {
    try {
      setShowDraftBanner(false)
      setError('')
      const files = await readClipboardImageFilesFromApi()
      if (files.length === 0) {
        setError('В буфере обмена не найдено изображение.')
        return
      }

      await addPhotosToTarget(files, target)
    } catch (clipboardError) {
      console.error('Failed to read clipboard images:', clipboardError)
      setError(
        toUserFacingErrorMessage(
          clipboardError,
          'Не удалось вставить изображение из буфера обмена. Скопируйте картинку и попробуйте снова.'
        )
      )
    }
  }

  const pastePhotos = async (event: ReactClipboardEvent<HTMLElement>, target: PhotoTarget) => {
    const clipboardFiles = await readClipboardImageFiles(event.clipboardData)
    if (clipboardFiles.length === 0) return

    event.preventDefault()
    await addPhotosToTarget(clipboardFiles, target)
  }

  const removePhoto = async (photo: TrainerChecklistPhoto, target: PhotoTarget) => {
    try {
      if (photo.photoId != null || photo.photoKey) {
        if (isSeniorPartnerMode) {
          await deleteSeniorTeacherReviewPhoto(photo)
        } else if (activeEntryKey) {
          await deleteMyTrainerReviewEntryPhoto(activeEntryKey, photo)
        } else {
          await deleteTrainerReviewPhoto(photo)
        }
      }

      if (target.kind === 'general') {
        setGeneralPhotos(prev => prev.filter(item => item.id !== photo.id))
      } else {
        setCriterionPhotos(prev => ({
          ...prev,
          [target.criterionCode]: (prev[target.criterionCode] ?? []).filter(item => item.id !== photo.id),
        }))
      }
      setSaveStatus('')
      setSaveWarning('')
    } catch (photoError) {
      console.error('Failed to delete photo:', photoError)
      setError(toUserFacingErrorMessage(photoError, 'Не удалось удалить фото'))
    }
  }

  const resetDraft = () => {
    if (!window.confirm('Сбросить текущий чек-лист? Несохранённые изменения будут потеряны.')) return

    if (isSeniorPartnerMode) {
      if (isSeniorEditing) {
        allowNextNavigationRef.current = true
        window.location.reload()
        return
      }
      setSelectedSeniorSessionItemId('')
      setSeniorManualMode(false)
      setSeniorManualOptionsError('')
      setSeniorLockedDirectionName('')
      setSelectedTeacherId(preselectedTeacherId && seniorTeacherOptions.some(option => option.teacherId === preselectedTeacherId)
        ? preselectedTeacherId
        : '')
      setSavedTeacherId('')
      setMeta(emptyTrainerChecklistMeta)
      setScores(createEmptyTrainerChecklistScores())
      setComments(createEmptyTrainerChecklistComments())
      setGeneralComment('')
      setGeneralPhotos([])
      setCriterionPhotos(createEmptyTrainerChecklistCriterionPhotos())
      setExpandedCriterionPhotoPanels({})
      setHiddenCriterionRows({})
      setSavedDraftSignature(createTrainerChecklistDraftSignature(createEmptyTrainerChecklistDraft()))
      setSaveStatus('')
      setSaveWarning('')
      setError('')
      return
    }

    clearTrainerChecklistDraft()
    if (activeDraftKey) clearTrainerChecklistDraft(activeDraftKey)
    setRecoveredReviewDraft(false)
    localStorage.removeItem(TRAINER_REVIEW_TEACHER_DRAFT_KEY)
    setActiveReviewId(null)
    setActiveEntryKey(null)
    setQueueAssignment(null)
    setSelectedTeacherId('')
    setSavedTeacherId('')
    setTeacherOptions([])
    setMeta(emptyTrainerChecklistMeta)
    setScores(createEmptyTrainerChecklistScores())
    setComments(createEmptyTrainerChecklistComments())
    setGeneralComment('')
    setGeneralPhotos([])
    setCriterionPhotos(createEmptyTrainerChecklistCriterionPhotos())
    setExpandedCriterionPhotoPanels({})
    setHiddenCriterionRows({})
    setOpenCalendar(null)
    setIsTrainingTimeOpen(false)
    setCalendarMonth(getCalendarMonthFromIso(getTodayIsoDate()))
    setShowAllTrainingHours(false)
    setHighlightMinutes(false)
    setSavedDraftSignature(createTrainerChecklistDraftSignature(createEmptyTrainerChecklistDraft()))
    setSaveStatus('')
    setSaveWarning('')
    setError('')
    allowNextNavigationRef.current = true
    navigate(pedagogyPaths.reviewNew, { replace: true })
  }

  const resolveDirectionId = async (): Promise<string> => {
    if (meta.directionId && meta.directionId !== TRAINER_REVIEW_OTHER_DIRECTION_ID) {
      return meta.directionId
    }

    const customName = meta.customDirectionName.trim()
    if (!customName) {
      throw new Error('Укажите направление')
    }

    const newDirectionId = await ensureTrainerReviewDirection(customName)
    setDirectionOptions(prev => (
      prev.some(option => option.directionId === newDirectionId)
        ? prev
        : [...prev, { directionId: newDirectionId, name: customName }].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    ))
    setMeta(prev => ({
      ...prev,
      directionId: newDirectionId,
      customDirectionName: '',
    }))
    return newDirectionId
  }

  const selectSeniorSession = (session: SeniorTeacherReviewSession) => {
    setSeniorManualMode(false)
    setSelectedSeniorSessionItemId(session.itemId)
    setSelectedTeacherId(session.teacherId)
    setSeniorLockedDirectionName(session.directionName || 'Другое')
    setMeta(prev => ({
      ...prev,
      studioId: String(session.studioInternalId),
      city: session.cityName,
      studioAddress: session.studioAddress,
      trainingDateTime: `${session.trainingDate}T${session.trainingTime.slice(0, 5)}`,
      directionId: session.directionId || TRAINER_REVIEW_OTHER_DIRECTION_ID,
      customDirectionName: session.directionId ? '' : session.directionName,
      coachName: session.teacherName,
    }))
    setSaveWarning('')
    setError('')
  }

  const enableSeniorManualMode = async () => {
    setSeniorManualMode(true)
    setSelectedSeniorSessionItemId('')
    setSeniorLockedDirectionName('')
    setSeniorManualOptionsError('')
    setMeta(prev => ({
      ...prev,
      studioId: '',
      city: '',
      studioAddress: '',
      trainingDateTime: '',
      directionId: '',
      customDirectionName: '',
    }))

    if (seniorManualOptions || seniorManualOptionsLoading) return

    setSeniorManualOptionsLoading(true)
    try {
      const options = await getSeniorTeacherManualReviewOptions(seniorAnchorStudioId)
      setSeniorManualOptions(options)
    } catch (manualError) {
      setSeniorManualOptionsError(friendlySeniorLoadError(manualError))
    } finally {
      setSeniorManualOptionsLoading(false)
    }
  }

  const disableSeniorManualMode = () => {
    setSeniorManualMode(false)
    setSeniorManualOptionsError('')
    setMeta(prev => ({
      ...prev,
      studioId: '',
      city: '',
      studioAddress: '',
      trainingDateTime: '',
      directionId: '',
      customDirectionName: '',
    }))
  }

  const saveReview = async () => {
    if (isSeniorPartnerMode && !isSeniorEditing && !seniorManualMode && !selectedSeniorSessionItemId) {
      failValidation('Выберите тренировку или включите ручной ввод.', studioFieldRef.current)
      return
    }

    if (!meta.studioId) {
      failValidation(
        isSeniorPartnerMode && seniorManualMode
          ? 'Выберите студию, в которой проходила тренировка.'
          : isSeniorPartnerMode
            ? 'Сначала выберите тренировку.'
            : 'Выберите студию, в которой проходила тренировка.',
        studioFieldRef.current
      )
      return
    }

    if (isSeniorPartnerMode && seniorManualMode && !isSeniorEditing) {
      const parts = splitTrainerReviewDateTime(meta.trainingDateTime)
      if (!parts.date || !parts.hour || !parts.minute) {
        failValidation('Укажите дату и время тренировки.', studioFieldRef.current)
        return
      }
      if (!meta.directionId && !meta.customDirectionName.trim()) {
        failValidation('Укажите направление тренировки.', studioFieldRef.current)
        return
      }
    }

    if (!selectedTeacherId && !isLegacyReviewWithoutTeacher) {
      failValidation('Выберите педагога из реестра.', teacherFieldRef.current)
      return
    }

    if (unevaluatedCount > 0) {
      // «Не оценено: 7» не говорит, КАКИЕ семь, — перечисляем поимённо.
      const shown = unevaluatedItems.slice(0, 3).map(item => item.label)
      const rest = unevaluatedItems.length - shown.length
      failValidation(
        `Заполните все оценки. Не оценено ${unevaluatedItems.length}: ${shown.join('; ')}${rest > 0 ? ` и ещё ${rest}` : ''}.`,
        criterionRowRefs.current[unevaluatedItems[0]?.id ?? ''] ?? null
      )
      return
    }

    if (generalPhotos.length < MIN_REVIEW_PHOTOS) {
      failValidation(REQUIRED_PHOTOS_MESSAGE, photosSectionRef.current)
      return
    }

    setSaving(true)
    setError('')
    setSaveStatus('')
    setSaveWarning('')

    try {
      const directionId = isSeniorPartnerMode ? '' : await resolveDirectionId()
      const scoreInput = Object.entries(scores).map(([criterionCode, score]) => ({
          criterionCode,
          score,
          comment: comments[criterionCode] ?? '',
        }))
      const wasPersonalEntry = activeEntryKey != null || assignmentKey != null
      let nextRecordKey: string
      let seniorSavedReviewId = activeSeniorReviewId

      if (isSeniorPartnerMode) {
        const result = seniorManualMode && !isSeniorEditing
          ? await saveSeniorTeacherManualReview({
              studioInternalId: seniorAnchorStudioId,
              teacherId: selectedTeacherId,
              targetStudioInternalId: Number(meta.studioId),
              trainingDate: splitTrainerReviewDateTime(meta.trainingDateTime).date,
              trainingTime: `${splitTrainerReviewDateTime(meta.trainingDateTime).hour}:${splitTrainerReviewDateTime(meta.trainingDateTime).minute}`,
              directionId: meta.directionId && meta.directionId !== TRAINER_REVIEW_OTHER_DIRECTION_ID ? meta.directionId : null,
              directionName: meta.directionId === TRAINER_REVIEW_OTHER_DIRECTION_ID ? meta.customDirectionName.trim() : null,
              generalComment: generalComment.trim() || null,
              scores: scoreInput,
            })
          : await saveSeniorTeacherReview({
              studioInternalId: seniorAnchorStudioId,
              itemId: isSeniorEditing ? null : selectedSeniorSessionItemId,
              reviewKey: activeSeniorReviewKey,
              generalComment: generalComment.trim() || null,
              scores: scoreInput,
            })
        nextRecordKey = result.reviewKey
        seniorSavedReviewId = result.reviewId
        setActiveSeniorReviewKey(result.reviewKey)
        setActiveSeniorReviewId(result.reviewId)
        setSelectedTeacherId(result.teacherId)
      } else if (activeEntryKey) {
        nextRecordKey = await upsertMyTrainerReviewEntry({
          entryKey: activeEntryKey,
          directionId,
          coachName: meta.coachName.trim(),
          teacherId: selectedTeacherId,
          generalComment: generalComment.trim() || null,
          scores: scoreInput,
        })
      } else if (assignmentKey != null) {
        nextRecordKey = await savePedagogyReviewAssignment({
          assignmentKey,
          directionId,
          coachName: meta.coachName.trim(),
          teacherId: selectedTeacherId,
          generalComment: generalComment.trim() || null,
          scores: scoreInput,
        })
        // С этого момента повторное сохранение должно редактировать созданную
        // запись, а не пытаться второй раз закрыть уже выполненное задание.
        setActiveEntryKey(nextRecordKey)
        setQueueAssignment(null)
      } else {
        nextRecordKey = await upsertTrainerReview({
          reviewId: activeReviewId,
          studioInternalId: meta.studioId ? Number(meta.studioId) : null,
          cityName: meta.city.trim() || null,
          studioAddress: meta.studioAddress.trim() || null,
          // Дата отсмотра ставится только при создании. У существующей записи её
          // трогать нельзя: review_date — ось периодного фильтра сводки.
          reviewDate: activeReviewId ? null : getTodayIsoDate(),
          trainingAt: meta.trainingDateTime || null,
          directionId,
          coachName: meta.coachName.trim(),
          teacherId: selectedTeacherId || null,
          generalComment: generalComment.trim() || null,
          scores: scoreInput,
        })
      }

      const readSavedDetail = () => (
        isSeniorPartnerMode
          ? getPartnerTrainerReviewDetail(Number(meta.studioId), nextRecordKey)
          : wasPersonalEntry
            ? getMyTrainerReviewEntryDetail(nextRecordKey)
            : getTrainerReviewDetail(nextRecordKey)
      )
      const detailBeforePhotoUpload = await readSavedDetail()
      if (!detailBeforePhotoUpload) {
        throw new Error('Не удалось перечитать сохранённый отсмотр')
      }

      const failedGeneralPhotoUploads: Array<{ photo: TrainerChecklistPhoto; reason: string }> = []
      const unsavedGeneralPhotos = generalPhotos.filter(photo => photo.file)
      for (const [index, photo] of unsavedGeneralPhotos.entries()) {
        try {
          if (isSeniorPartnerMode) {
            if (!seniorSavedReviewId) throw new Error('Не удалось определить senior-отсмотр для фото')
            await uploadSeniorTeacherReviewPhoto({
              reviewId: seniorSavedReviewId,
              reviewKey: nextRecordKey,
              photo,
              sortOrder: index,
            })
          } else if (wasPersonalEntry) {
            await uploadMyTrainerReviewEntryPhoto({ entryKey: nextRecordKey, photo, sortOrder: index })
          } else {
            await uploadTrainerReviewPhoto({ reviewId: nextRecordKey, photo, sortOrder: index })
          }
        } catch (photoError) {
          console.error('Failed to upload trainer review photo:', photoError)
          failedGeneralPhotoUploads.push({
            photo,
            reason: toUserFacingErrorMessage(photoError, 'Не удалось загрузить фото'),
          })
        }
      }

      const failedCriterionPhotoUploads: Record<string, Array<{ photo: TrainerChecklistPhoto; reason: string }>> = {}
      for (const [criterionCode, photosForCriterion] of Object.entries(criterionPhotos)) {
        const unsavedCriterionPhotos = photosForCriterion.filter(photo => photo.file)
        if (unsavedCriterionPhotos.length === 0) continue

        const reviewScoreId = detailBeforePhotoUpload.scoreReviewIds[criterionCode]
        if (!wasPersonalEntry && !reviewScoreId) {
          failedCriterionPhotoUploads[criterionCode] = unsavedCriterionPhotos.map(photo => ({
            photo,
            reason: 'Не удалось определить пункт для сохранения фото',
          }))
          continue
        }

        for (const [index, photo] of unsavedCriterionPhotos.entries()) {
          try {
            if (isSeniorPartnerMode) {
              if (!seniorSavedReviewId) throw new Error('Не удалось определить senior-отсмотр для фото')
              await uploadSeniorTeacherReviewPhoto({
                reviewId: seniorSavedReviewId,
                reviewKey: nextRecordKey,
                reviewScoreId,
                photo,
                sortOrder: index,
              })
            } else if (wasPersonalEntry) {
              await uploadMyTrainerReviewEntryPhoto({
                entryKey: nextRecordKey,
                criterionCode,
                photo,
                sortOrder: index,
              })
            } else {
              await uploadTrainerReviewPhoto({
                reviewId: nextRecordKey,
                reviewScoreId,
                photo,
                sortOrder: index,
              })
            }
          } catch (photoError) {
            console.error('Failed to upload criterion trainer review photo:', photoError)
            failedCriterionPhotoUploads[criterionCode] ??= []
            failedCriterionPhotoUploads[criterionCode].push({
              photo,
              reason: toUserFacingErrorMessage(photoError, 'Не удалось загрузить фото'),
            })
          }
        }
      }

      const detail = await readSavedDetail()
      if (!detail) {
        throw new Error('Не удалось перечитать сохранённый отсмотр')
      }

      const nextDraft = normalizeTrainerChecklistDraft({
        meta: {
          ...detail.meta,
          customDirectionName: '',
        },
        scores: detail.scores,
        comments: detail.comments,
        generalComment: detail.generalComment,
        generalPhotos: detail.generalPhotos,
        criterionPhotos: detail.criterionPhotos,
        hiddenCriterionRows: deriveHiddenCriterionRows(detail.scores),
      })

      if (isSeniorPartnerMode) {
        setActiveSeniorReviewKey(nextRecordKey)
        setActiveReviewId(null)
        setActiveEntryKey(null)
      } else if (wasPersonalEntry) {
        setActiveEntryKey(nextRecordKey)
        setActiveReviewId(null)
      } else {
        setActiveReviewId(nextRecordKey)
      }
      setMeta(nextDraft.meta)
      setScores(nextDraft.scores)
      setComments(nextDraft.comments)
      setGeneralComment(nextDraft.generalComment)
      setGeneralPhotos([
        ...nextDraft.generalPhotos,
        ...failedGeneralPhotoUploads.map(entry => entry.photo),
      ])
      setCriterionPhotos({
        ...nextDraft.criterionPhotos,
        ...Object.fromEntries(
          Object.entries(failedCriterionPhotoUploads).map(([criterionCode, entries]) => [
            criterionCode,
            [
              ...(nextDraft.criterionPhotos[criterionCode] ?? []),
              ...entries.map(entry => entry.photo),
            ],
          ])
        ),
      })
      setExpandedCriterionPhotoPanels({})
      setHiddenCriterionRows(deriveHiddenCriterionRows(nextDraft.scores, nextDraft.hiddenCriterionRows))
      setSavedDraftSignature(createTrainerChecklistDraftSignature(nextDraft))
      setSavedTeacherId(selectedTeacherId)
      clearTrainerChecklistDraft()
      if (activeDraftKey) clearTrainerChecklistDraft(activeDraftKey)
      clearTrainerChecklistDraft(nextRecordKey)
      localStorage.removeItem(TRAINER_REVIEW_TEACHER_DRAFT_KEY)
      setShowDraftBanner(false)
      setRecoveredReviewDraft(false)
      const failedPhotosCount = failedGeneralPhotoUploads.length
        + Object.values(failedCriterionPhotoUploads).reduce((sum, entries) => sum + entries.length, 0)
      if (failedPhotosCount > 0) {
        setSaveWarning(
          failedPhotosCount === 1
            ? 'Отсмотр сохранён, но фото не загрузилось. Оно не появится при повторном открытии карточки.'
            : `Отсмотр сохранён, но не загрузились ${failedPhotosCount} фото. Они не появятся при повторном открытии карточки.`
        )
      } else {
        setSaveStatus('Отсмотр сохранён.')
      }

      if (failedPhotosCount === 0) {
        allowNextNavigationRef.current = true
        if (isSeniorPartnerMode) {
          const teacherForReport = detail.teacherId || selectedTeacherId
          navigate(
            `/partners/${partnerNumber}/studios/${detail.meta.studioId}/teachers/${teacherForReport}/reviews/${nextRecordKey}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
            { replace: true }
          )
        } else if (assignmentKey && returnTo) {
          navigate(returnTo, {
            replace: true,
            state: { pedagogyReviewSaved: true },
          })
        } else {
          navigate(
            wasPersonalEntry
              ? `${pedagogyPaths.reviewNew}?entryKey=${encodeURIComponent(nextRecordKey)}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`
              : createTrainerChecklistReviewLink(nextRecordKey),
            { replace: true }
          )
        }
      }
    } catch (saveError) {
      console.error('Failed to save trainer review:', saveError)
      setError(toUserFacingErrorMessage(saveError, 'Не удалось сохранить отсмотр'))
    } finally {
      setSaving(false)
    }
  }

  const createTeacherFromAssignment = async () => {
    if (!assignmentKey || !queueAssignment) return
    setCreatingTeacher(true)
    setCreateTeacherError('')
    try {
      const result = await createTeacherForPedagogyReviewAssignment(assignmentKey, newTeacherName)
      const option: TrainerReviewTeacherOption = {
        teacherId: result.teacherId,
        fullName: result.fullName,
        status: result.status,
        partnerNumbers: [],
        citiesText: queueAssignment.cityName,
      }
      setTeacherOptions(current => (
        current.some(item => item.teacherId === option.teacherId)
          ? current
          : [...current, option].sort((a, b) => a.fullName.localeCompare(b.fullName, 'ru'))
      ))
      setSelectedTeacherId(option.teacherId)
      updateMeta('coachName', option.fullName)
      setShowCreateTeacher(false)
      setSaveStatus(result.created
        ? 'Карточка педагога создана и выбрана.'
        : 'Такая карточка уже была в реестре и выбрана.')
    } catch (createError) {
      setCreateTeacherError(createError instanceof Error ? createError.message : 'Не удалось добавить педагога')
    } finally {
      setCreatingTeacher(false)
    }
  }

  const openReport = (recordKey: string) => {
    if (hasUnsavedReviewChanges || saving) return

    if (isSeniorPartnerMode) {
      if (!selectedTeacherId) return
      navigate(
        `/partners/${partnerNumber}/studios/${meta.studioId || seniorStudioIdParam}/teachers/${selectedTeacherId}/reviews/${recordKey}${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`
      )
      return
    }

    openTrainerChecklistReport(recordKey, returnTo || undefined)
  }

  if (loading) {
    return (
      <div className="space-y-4 pb-8">
        <Card className="border border-border shadow-sm">
          <div className="text-sm text-muted-foreground">Загрузка формы отсмотра...</div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-36">
      <PedagogyBackLink
        label={returnTo ? 'Назад' : isSeniorPartnerMode ? 'К педагогам' : 'К отсмотрам'}
        onClick={() => navigateWithUnsavedConfirmation(
          returnTo || (isSeniorPartnerMode
            ? `/partners/${partnerNumber}/studios/${seniorStudioIdParam}/teachers`
            : pedagogyPaths.reviews)
        )}
      />
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            Отсмотр тренировки
          </div>
          <h1 className="mt-1 text-2xl font-bold tracking-tight">
            {activeRecordKey ? 'Карточка отсмотра' : 'Новый отсмотр'}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
              {/*
                Верхняя и нижняя кнопки — одно и то же действие, поэтому и
                блокировка одна: раньше верхняя гасла при `!hasUnsavedReviewChanges`,
                нижняя нет, и на один и тот же вопрос «можно ли сохранить?» форма
                отвечала двумя разными способами на одном экране.
              */}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void saveReview()}
                disabled={saving}
                title={!hasUnsavedReviewChanges ? 'Несохранённых изменений нет' : undefined}
              >
                <Save className="mr-1.5 inline h-4 w-4" />
                {saving ? 'Сохраняем...' : 'Сохранить изменения'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => activeRecordKey && openReport(activeRecordKey)}
                disabled={!canDownloadReport}
                title={!canDownloadReport ? 'Сначала сохраните изменения' : undefined}
              >
                <Download className="mr-1.5 inline h-4 w-4" />
                Скачать отчёт
              </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={resetDraft}
          >
            <RotateCcw className="mr-1.5 inline h-4 w-4" />
            Сброс
          </Button>
        </div>
      </div>

      {/*
        Ошибка валидации живёт в подвале формы, в 4 000 пикселях от верхней
        кнопки. Скролл к виновному контролу уводит от неё ещё дальше, поэтому
        сообщение дублируется здесь — рядом с кнопкой, которую нажали.
      */}
      {(error || saveStatus) && (
        <Card className={`border shadow-sm ${error ? 'border-error/30 bg-error/5' : 'border-success/30 bg-success/5'}`}>
          <div
            role={error ? 'alert' : 'status'}
            aria-live={error ? 'assertive' : 'polite'}
            className={`text-sm ${error ? 'text-error' : 'text-success'}`}
          >
            {error || saveStatus}
          </div>
        </Card>
      )}

      {!isSeniorPartnerMode && (
        <Card className="border border-primary/20 bg-primary/5 shadow-sm">
          <div className="text-sm text-foreground">
            Результат франчайзи получит в кабинете автоматически; отправлять отчёт вручную не нужно.
          </div>
        </Card>
      )}

      {recoveredReviewDraft && (
        <Card className="border border-amber-300 bg-amber-50 shadow-sm">
          <div className="flex flex-col gap-3 text-sm text-amber-900 md:flex-row md:items-center md:justify-between">
            <div>
              Восстановлены несохранённые правки этого отсмотра. На сервере их ещё нет —
              нажмите «Сохранить», иначе они останутся только в этом браузере.
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                if (!window.confirm('Отбросить восстановленные правки и вернуться к сохранённой версии?')) return
                if (activeDraftKey) clearTrainerChecklistDraft(activeDraftKey)
                setRecoveredReviewDraft(false)
                /*
                  Перечитываем карточку с сервера. Черновика уже нет, поэтому
                  загрузка отдаст сохранённое состояние. Перезагрузка, а не
                  локальный сброс: состояние формы разложено по дюжине useState
                  (оценки, комментарии, фото, скрытые строки, подпись), и
                  собирать «вернуть как было» вручную — это второй загрузчик,
                  который разъедется с настоящим.
                  allowNextNavigation снимает beforeunload: уход намеренный.
                */
                allowNextNavigationRef.current = true
                window.location.reload()
              }}
            >
              Отбросить правки
            </Button>
          </div>
        </Card>
      )}

      {showDraftBanner && (
        <Card className="border border-amber-300 bg-amber-50 shadow-sm">
          <div className="flex flex-col gap-3 text-sm text-amber-900 md:flex-row md:items-center md:justify-between">
            <div>Есть несохранённый черновик. Можно продолжить его редактирование.</div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  allowNextNavigationRef.current = true
                  navigate(createTrainerChecklistDraftLink())
                }}
              >
                Вернуться к черновику
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  clearTrainerChecklistDraft()
                  localStorage.removeItem(TRAINER_REVIEW_TEACHER_DRAFT_KEY)
                  setShowDraftBanner(false)
                }}
              >
                Начать заново
              </Button>
            </div>
          </div>
        </Card>
      )}

      {(
        <>
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_320px]">
        {isSeniorPartnerMode ? (
          <Card className="border border-border shadow-sm">
            <div className="mb-4">
              <div className="font-semibold">Что смотрим</div>
              <div className="mt-1 text-xs text-muted-foreground">
                Выберите педагога и тренировку. Если нужного занятия нет — введите данные вручную.
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {!isSeniorEditing && seniorContext && seniorContext.cities.length > 1 && (
                <label className="space-y-1.5 text-sm">
                  <span className="font-medium text-muted-foreground">Город</span>
                  <select
                    className="input w-full"
                    value={selectedSeniorCityId}
                    onChange={event => {
                      const nextCityId = event.target.value
                      setSelectedSeniorCityId(nextCityId)
                      setSelectedSeniorSessionItemId('')
                      setSeniorLockedDirectionName('')
                      const nextCityNumber = Number(nextCityId)
                      const currentTeacher = seniorContext.teachers.find(item => item.teacherId === selectedTeacherId)
                      const keepTeacher = Boolean(
                        selectedTeacherId
                        && currentTeacher
                        && (!nextCityId || (currentTeacher.cityIds ?? []).includes(nextCityNumber))
                      )
                      if (!keepTeacher) setSelectedTeacherId('')
                      setMeta(prev => ({
                        ...prev,
                        studioId: '',
                        city: seniorContext.cities.find(city => String(city.cityId) === nextCityId)?.cityName ?? '',
                        studioAddress: '',
                        trainingDateTime: '',
                        directionId: '',
                        customDirectionName: '',
                        coachName: keepTeacher ? prev.coachName : '',
                      }))
                    }}
                  >
                    <option value="">Все города</option>
                    {seniorContext.cities.map(city => (
                      <option key={city.cityId} value={String(city.cityId)}>{city.cityName}</option>
                    ))}
                  </select>
                </label>
              )}

              <div ref={teacherFieldRef} className="space-y-1.5 text-sm">
                <span className="font-medium text-muted-foreground">Педагог *</span>
                {isSeniorEditing ? (
                  <div className="flex min-h-10 items-center rounded-lg border border-border bg-muted/30 px-3 text-sm font-medium">
                    {meta.coachName || '—'}
                  </div>
                ) : (
                  <SearchableSelect
                    value={selectedTeacherId}
                    placeholder="Выберите педагога"
                    searchPlaceholder="Поиск по ФИО"
                    emptyMessage="Педагоги не найдены"
                    options={seniorTeacherOptions.map(option => ({
                      value: option.teacherId,
                      label: option.fullName,
                      description: [teacherStatusLabel(option.status), option.citiesText || null].filter(Boolean).join(' · '),
                    }))}
                    onChange={teacherId => {
                      const option = seniorContext?.teachers.find(item => item.teacherId === teacherId)
                      setSelectedTeacherId(teacherId)
                      setSelectedSeniorSessionItemId('')
                      setSeniorLockedDirectionName('')
                      setMeta(prev => ({
                        ...prev,
                        studioId: '',
                        studioAddress: '',
                        trainingDateTime: '',
                        directionId: '',
                        customDirectionName: '',
                        coachName: option?.fullName ?? '',
                      }))
                    }}
                  />
                )}
              </div>

              {!isSeniorEditing && !seniorManualMode && (
                <div ref={setStudioFieldRef} className="space-y-1.5 text-sm">
                  <span className="font-medium text-muted-foreground">Тренировка *</span>
                  <select
                    className="input w-full"
                    value={selectedSeniorSessionItemId}
                    disabled={!selectedTeacherId || seniorSessionsLoading || seniorSessionsForTeacher.length === 0}
                    onChange={event => {
                      const session = seniorSessionsForTeacher.find(item => item.itemId === event.target.value)
                      if (session) selectSeniorSession(session)
                      else setSelectedSeniorSessionItemId('')
                    }}
                  >
                    <option value="">
                      {!selectedTeacherId
                        ? 'Сначала выберите педагога'
                        : seniorSessionsLoading
                          ? 'Загружаем тренировки…'
                          : 'Выберите тренировку'}
                    </option>
                    {seniorSessionsForTeacher.map(session => (
                      <option key={session.itemId} value={session.itemId}>
                        {formatTrainerReviewDate(session.trainingDate)} · {session.trainingTime.slice(0, 5)} · {session.directionName || 'Направление не указано'}
                      </option>
                    ))}
                  </select>
                  {selectedTeacherId && !seniorSessionsLoading && !seniorSessionsError && seniorSessionsForTeacher.length === 0 && (
                    <span className="block text-xs text-muted-foreground">Подходящих занятий за последние 7 дней не найдено.</span>
                  )}
                  {seniorSessionsError && <span className="block text-xs text-muted-foreground">{seniorSessionsError}</span>}
                  <button
                    type="button"
                    className="text-xs font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!selectedTeacherId}
                    onClick={() => void enableSeniorManualMode()}
                  >
                    Не нашли тренировку? Ввести данные вручную
                  </button>
                </div>
              )}
            </div>

            {!isSeniorEditing && seniorManualMode && (
              <div ref={setStudioFieldRef} className="mt-4 rounded-lg border border-border bg-muted/15 p-3">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold">Данные тренировки вручную</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">Используйте, если занятие не нашлось или педагог не сматчился с Listok.</div>
                  </div>
                  <button type="button" className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline" onClick={disableSeniorManualMode}>
                    Вернуться к списку тренировок
                  </button>
                </div>

                {seniorManualOptionsLoading ? (
                  <div className="text-sm text-muted-foreground">Загружаем студии и направления…</div>
                ) : seniorManualOptionsError ? (
                  <div className="text-sm text-error">{seniorManualOptionsError}</div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <label className="space-y-1.5 text-sm md:col-span-2">
                      <span className="font-medium text-muted-foreground">Студия *</span>
                      <select
                        className="input w-full"
                        value={meta.studioId}
                        onChange={event => {
                          const studio = seniorManualStudios.find(item => String(item.studioInternalId) === event.target.value)
                          setMeta(prev => ({
                            ...prev,
                            studioId: studio ? String(studio.studioInternalId) : '',
                            city: studio?.cityName ?? '',
                            studioAddress: studio?.address ?? '',
                          }))
                        }}
                      >
                        <option value="">Выберите студию</option>
                        {seniorManualStudios.map(studio => (
                          <option key={studio.studioInternalId} value={String(studio.studioInternalId)}>
                            {[studio.cityName, studio.studioNumber ? `№${studio.studioNumber}` : null, studio.address].filter(Boolean).join(' · ')}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="space-y-1.5 text-sm">
                      <span className="font-medium text-muted-foreground">Дата *</span>
                      <input
                        type="date"
                        className="input w-full"
                        value={trainingDateTimeParts.date}
                        min={getDaysAgoIsoDate(6)}
                        max={getTodayIsoDate()}
                        onChange={event => {
                          setMeta(prev => ({
                            ...prev,
                            trainingDateTime: composeTrainerReviewDateTime(event.target.value, trainingDateTimeParts.hour || '00', trainingDateTimeParts.minute || '00'),
                          }))
                        }}
                      />
                    </label>

                    <label className="space-y-1.5 text-sm">
                      <span className="font-medium text-muted-foreground">Время *</span>
                      <input
                        type="time"
                        step={300}
                        className="input w-full"
                        disabled={!trainingDateTimeParts.date}
                        value={trainingDateTimeParts.date ? `${trainingDateTimeParts.hour}:${trainingDateTimeParts.minute}` : ''}
                        onChange={event => {
                          const [hour = '00', minute = '00'] = event.target.value.split(':')
                          setMeta(prev => ({
                            ...prev,
                            trainingDateTime: composeTrainerReviewDateTime(trainingDateTimeParts.date, hour, minute),
                          }))
                        }}
                      />
                    </label>

                    <label className="space-y-1.5 text-sm md:col-span-2">
                      <span className="font-medium text-muted-foreground">Направление *</span>
                      <select
                        className="input w-full"
                        value={meta.directionId}
                        onChange={event => {
                          const value = event.target.value
                          setMeta(prev => ({
                            ...prev,
                            directionId: value,
                            customDirectionName: value === TRAINER_REVIEW_OTHER_DIRECTION_ID ? prev.customDirectionName : '',
                          }))
                        }}
                      >
                        <option value="">Выберите направление</option>
                        {(seniorManualOptions?.directions ?? []).map(direction => (
                          <option key={direction.directionId} value={direction.directionId}>{direction.name}</option>
                        ))}
                        <option value={TRAINER_REVIEW_OTHER_DIRECTION_ID}>Другое</option>
                      </select>
                    </label>

                    {meta.directionId === TRAINER_REVIEW_OTHER_DIRECTION_ID && (
                      <label className="space-y-1.5 text-sm md:col-span-2">
                        <span className="font-medium text-muted-foreground">Название направления *</span>
                        <input
                          className="input w-full"
                          value={meta.customDirectionName}
                          onChange={event => updateMeta('customDirectionName', event.target.value)}
                          placeholder="Введите направление"
                        />
                      </label>
                    )}

                  </div>
                )}
              </div>
            )}

            {(isSeniorEditing || selectedSeniorSession) ? (
              <div className="mt-4 grid gap-2 rounded-lg bg-muted/35 p-3 sm:grid-cols-2 xl:grid-cols-4">
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Город</div>
                  <div className="mt-1 text-sm font-medium">{meta.city || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Студия</div>
                  <div className="mt-1 text-sm font-medium">{meta.studioAddress || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Дата и время</div>
                  <div className="mt-1 text-sm font-medium">
                    {meta.trainingDateTime
                      ? `${formatTrainerReviewDate(trainingDateTimeParts.date)} · ${trainingDateTimeParts.hour || '00'}:${trainingDateTimeParts.minute || '00'}`
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Направление</div>
                  <div className="mt-1 text-sm font-medium">{seniorLockedDirectionName || '—'}</div>
                </div>
              </div>
            ) : !seniorManualMode ? (
              <div className="mt-4 rounded-lg bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Покажем студию, время и направление после выбора тренировки.
              </div>
            ) : null}
          </Card>
        ) : (
        <Card className="border border-border shadow-sm">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <label ref={setStudioFieldRef} className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Студия *</span>
              {isQueueBound ? (
                <input
                  className="input w-full bg-muted/30"
                  value={selectedStudio ? buildStudioLabel(selectedStudio) : `Студия #${meta.studioId}`}
                  readOnly
                />
              ) : (
                <SearchableStudioPicker
                  studios={studios}
                  selectedStudioId={meta.studioId}
                  query={studioQuery}
                  onQueryChange={setStudioQuery}
                  onChange={studio => {
                    if (!studio) {
                      setMeta(prev => ({
                        ...prev,
                        studioId: '',
                        city: '',
                        studioAddress: '',
                      }))
                      setSelectedTeacherId('')
                      return
                    }

                    setMeta(prev => ({
                      ...prev,
                      studioId: String(studio.studio_internal_id),
                      city: studio.city?.city_name ?? '',
                      studioAddress: studio.address ?? '',
                    }))
                  }}
                />
              )}
              <span className="block text-xs text-muted-foreground">После выбора студии можно найти работающего педагога, педагога в обучении или кандидата.</span>
            </label>

            <label className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Город</span>
              <input className="input w-full bg-muted/30" value={meta.city} readOnly placeholder="Заполнится по студии" />
            </label>

            <label className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Адрес студии</span>
              <input className="input w-full bg-muted/30" value={meta.studioAddress} readOnly placeholder="Заполнится по студии" />
            </label>

            <div ref={teacherFieldRef} className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">
                Педагог{isLegacyReviewWithoutTeacher ? '' : ' *'}
              </span>
              <SearchableSelect
                value={selectedTeacherId}
                disabled={teacherOptionsLoading || !meta.studioId}
                placeholder={
                  !meta.studioId
                    ? 'Сначала выберите студию'
                    : teacherOptionsLoading
                      ? 'Загружаем педагогов…'
                      : isLegacyReviewWithoutTeacher && meta.coachName.trim()
                        ? meta.coachName.trim()
                        : 'Выберите педагога'
                }
                searchPlaceholder="Поиск по ФИО, городу или статусу"
                emptyMessage="Подходящие педагоги не найдены"
                options={teacherOptions.map(option => ({
                  value: option.teacherId,
                  label: option.fullName,
                  description: [
                    teacherStatusLabel(option.status),
                    option.citiesText || null,
                    option.matchedAliases?.length
                      ? `уже связан как ${option.matchedAliases.map(alias => `«${alias}»`).join(', ')}`
                      : null,
                  ].filter(Boolean).join(' · '),
                }))}
                onChange={teacherId => {
                  const option = teacherOptions.find(item => item.teacherId === teacherId)
                  setSelectedTeacherId(teacherId)
                  if (option) updateMeta('coachName', option.fullName)
                }}
              />
              {isLegacyReviewWithoutTeacher && !selectedTeacherId && meta.coachName.trim() && (
                <span className="block text-xs text-muted-foreground">
                  Старый отсмотр сохранён по имени «{meta.coachName.trim()}». Его можно оставить без привязки или выбрать карточку педагога.
                </span>
              )}
              {queueTeacherLinkNotice && (
                <span className={`block text-xs ${queueTeacherLinkNotice.kind === 'change' ? 'text-warning' : 'text-muted-foreground'}`}>
                  {queueTeacherLinkNotice.text}
                </span>
              )}
              {teacherOptionsError && <span className="block text-xs text-error">{teacherOptionsError}</span>}
              {!teacherOptionsLoading && Boolean(meta.studioId) && teacherOptions.length === 0 && (
                <span className="block text-xs text-muted-foreground">В реестре нет доступных работающих педагогов, педагогов в обучении или кандидатов.</span>
              )}
              {queueAssignment && (
                <div className="space-y-2 pt-1">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                    disabled={creatingTeacher}
                    onClick={() => {
                      setShowCreateTeacher(current => !current)
                      setCreateTeacherError('')
                      if (!newTeacherName.trim()) setNewTeacherName(queueAssignment.teacherNameRaw?.trim() ?? '')
                    }}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Педагога нет в реестре — добавить
                  </button>
                  {showCreateTeacher && (
                    <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
                      <label className="block space-y-1">
                        <span className="text-xs font-medium text-muted-foreground">ФИО новой карточки</span>
                        <input
                          className="input w-full"
                          value={newTeacherName}
                          disabled={creatingTeacher}
                          placeholder="Фамилия Имя"
                          onChange={event => setNewTeacherName(event.target.value)}
                        />
                      </label>
                      <p className="text-xs text-muted-foreground">
                        Карточка будет создана со статусом «Работает» и привязана к франшизе и городу этого задания. Сначала проверьте поиск, чтобы не создать дубль.
                      </p>
                      {createTeacherError && <p className="text-xs text-error">{createTeacherError}</p>}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={creatingTeacher || newTeacherName.trim().length < 5}
                          onClick={() => void createTeacherFromAssignment()}
                        >
                          {creatingTeacher ? 'Добавляем…' : 'Создать и выбрать'}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={creatingTeacher}
                          onClick={() => setShowCreateTeacher(false)}
                        >
                          Отмена
                        </Button>
                      </div>
                    </div>
                  )}
                  <span className="block text-xs text-muted-foreground">
                    Выбор сохранит подтверждённую связь с именем из расписания для следующих заданий этой студии.
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Дата, время тренировки</span>
              {isQueueBound ? (
                <input
                  className="input w-full bg-muted/30"
                  value={`${formatTrainerReviewDate(trainingDateTimeParts.date)} · ${trainingDateTimeParts.hour || '09'}:${trainingDateTimeParts.minute || '00'}`}
                  readOnly
                />
              ) : (
              <div className="grid gap-2 xl:grid-cols-[minmax(0,150px)_minmax(0,1fr)]">
                <CalendarDateField
                  label=""
                  value={trainingDateTimeParts.date}
                  target="training"
                  openTarget={openCalendar}
                  calendarMonth={calendarMonth}
                  onOpen={openDateCalendar}
                  onMonthChange={setCalendarMonth}
                  onChange={updateTrainingDate}
                />
                <TrainingTimeField
                  hour={trainingDateTimeParts.hour || '09'}
                  minute={trainingDateTimeParts.minute || '00'}
                  isOpen={isTrainingTimeOpen}
                  showAllHours={showAllTrainingHours}
                  highlightMinutes={highlightMinutes}
                  onOpenChange={nextIsOpen => {
                    setIsTrainingTimeOpen(nextIsOpen)
                    setShowAllTrainingHours(false)
                    if (nextIsOpen) setOpenCalendar(null)
                  }}
                  onToggleAllHours={() => setShowAllTrainingHours(value => !value)}
                  onHourSelect={selectTrainingHour}
                  onMinuteSelect={selectTrainingMinute}
                />
              </div>
              )}
            </div>

            <label className="space-y-1.5 text-sm">
              <span className="font-medium text-muted-foreground">Направление *</span>
              <select
                className="input w-full"
                value={meta.directionId}
                onChange={event => {
                  const value = event.target.value
                  setMeta(prev => ({
                    ...prev,
                    directionId: value,
                    customDirectionName: value === TRAINER_REVIEW_OTHER_DIRECTION_ID ? prev.customDirectionName : '',
                  }))
                }}
              >
                <option value="">Выберите направление</option>
                {directionOptions.map(option => (
                  <option key={option.directionId} value={option.directionId}>{option.name}</option>
                ))}
                <option value={TRAINER_REVIEW_OTHER_DIRECTION_ID}>Другое</option>
              </select>
            </label>

            {meta.directionId === TRAINER_REVIEW_OTHER_DIRECTION_ID && (
              <label className="space-y-1.5 text-sm md:col-span-2 xl:col-span-3">
                <span className="font-medium text-muted-foreground">Новое направление</span>
                <input className="input w-full" value={meta.customDirectionName} onChange={event => updateMeta('customDirectionName', event.target.value)} placeholder="Введите название направления" />
              </label>
            )}
          </div>
        </Card>
        )}

        <Card className="border border-border shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-muted-foreground">Общее количество баллов</div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums">{totalScore}</span>
                <span className="text-sm text-muted-foreground">из {maxScore}</span>
              </div>
            </div>
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${qualityPercent >= 100 ? 'bg-success/10 text-success' : 'bg-primary/15 text-primary-foreground'}`}>
              {qualityPercent >= 100 ? <Award className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5 text-amber-700" />}
            </span>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-muted-foreground">Качество</span>
              <span className="font-semibold tabular-nums">{formatPercent(qualityPercent)}</span>
            </div>
            <ProgressBar value={qualityPercent} />
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{missingScore > 0 ? `Не хватает ${missingScore} балл(ов) до максимума.` : conclusion}</span>
              <span className={unevaluatedCount > 0 ? 'font-semibold text-amber-700' : 'text-success'}>
                Оценено: {evaluatedCount} из {totalCriteriaCount}
              </span>
            </div>
            {unevaluatedCount > 0 && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full"
                onClick={fillRemainingWithMaxScores}
              >
                <Award className="mr-1.5 inline h-4 w-4" />
                {evaluatedCount === 0
                  ? 'Все максимум'
                  : `Остальным максимум · ${unevaluatedCount}`}
              </Button>
            )}
          </div>
          <div className={`mt-4 rounded-lg border px-3 py-2 text-sm font-medium ${
            qualityPercent >= 100
              ? 'border-success/30 bg-success/5 text-success'
              : qualityPercent < 50
                ? 'border-error/30 bg-error/5 text-error'
                : 'border-amber-300 bg-amber-50 text-amber-900'
          }`}>
            {conclusion}
          </div>
          <div className="mt-4 text-xs text-muted-foreground">Фото: {totalPhotoCount}</div>
        </Card>
      </div>

      {TRAINER_CHECKLIST_SECTIONS.map(section => (
        <section key={section.id} className="overflow-hidden rounded-card border border-border bg-card shadow-card">
          <div className="border-b border-border bg-muted/80 px-4 py-3">
            <h2 className="text-base font-semibold">{section.title}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] border-collapse text-sm">
              <thead className="tg-table-header text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-[44%] px-4 py-3 text-left font-semibold">Критерий</th>
                  <th className="w-64 px-3 py-3 text-center font-semibold">Оценка</th>
                  <th className="w-[32%] px-3 py-3 text-left font-semibold">Комментарий</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {section.items.map(item => (
                  <tr
                    key={item.id}
                    ref={element => { criterionRowRefs.current[item.id] = element }}
                    className={`align-top ${hiddenCriterionRows[item.id] ? 'bg-muted/25 text-muted-foreground' : ''}`}
                  >
                    <td className="px-4 py-3 leading-snug">
                      <div className="space-y-2">
                        <div>{item.label}</div>
                        {item.id === 'admin_desk' && (
                          <button
                            type="button"
                            onClick={() => toggleHiddenCriterionRow(item.id)}
                            className={`inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
                              hiddenCriterionRows[item.id]
                                ? 'border-amber-300 bg-amber-50 text-amber-900'
                                : 'border-border bg-background text-muted-foreground hover:bg-muted'
                            }`}
                          >
                            {hiddenCriterionRows[item.id] ? 'Вернуть пункт' : 'Пропустить пункт'}
                          </button>
                        )}
                        {hiddenCriterionRows[item.id] && (
                          <div className="text-[11px] text-muted-foreground">
                            Пункт не оценивается для этой тренировки и будет пропущен при заполнении.
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      {hiddenCriterionRows[item.id] ? (
                        <div className="rounded-lg border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
                          Пункт пропущен
                        </div>
                      ) : (
                        <ScoreButtonGroup
                          value={scores[item.id]}
                          maxScore={item.maxScore}
                          label={item.label}
                          onChange={value => updateScore(item.id, value)}
                        />
                      )}
                      {!hiddenCriterionRows[item.id] && scores[item.id] == null && !isOptionalTrainerChecklistCriterion(item.id) && (
                        <div className="mt-1 text-center text-[11px] font-medium text-amber-700">Не оценено</div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="relative">
                        {activeCommentField === `criterion:${item.id}` && !hiddenCriterionRows[item.id] && (
                          <div className="mb-1 flex items-center justify-end">
                            <div className="flex items-center gap-2">
                              {hasCommentFormattingSyntax(comments[item.id] ?? '') && (
                                <button
                                  type="button"
                                  className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                                  onMouseDown={event => event.preventDefault()}
                                  onClick={() => setPreviewCommentField(prev => prev === `criterion:${item.id}` ? null : `criterion:${item.id}`)}
                                >
                                  {previewCommentField === `criterion:${item.id}` ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                                </button>
                              )}
                              <CommentFormatToolbar
                                disabled={hiddenCriterionRows[item.id]}
                                onFormat={definition => applyFormatToTextarea(
                                  criterionCommentRefs.current[item.id],
                                  comments[item.id] ?? '',
                                  value => updateComment(item.id, value),
                                  definition
                                )}
                              />
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <textarea
                            ref={element => {
                              criterionCommentRefs.current[item.id] = element
                              resizeCommentTextarea(element)
                            }}
                            value={comments[item.id] ?? ''}
                            onChange={event => {
                              updateComment(item.id, event.target.value)
                              resizeCommentTextarea(event.currentTarget)
                            }}
                            onFocus={() => focusCriterionCommentField(item.id)}
                            onBlur={event => {
                              if (event.relatedTarget instanceof Node && event.currentTarget.parentElement?.contains(event.relatedTarget)) {
                                return
                              }
                              setActiveCommentField(prev => (prev === `criterion:${item.id}` ? null : prev))
                              setPreviewCommentField(prev => (prev === `criterion:${item.id}` ? null : prev))
                            }}
                            className="input min-h-[42px] max-h-60 flex-1 resize-none overflow-y-auto text-sm"
                            aria-label={`Комментарий: ${item.label}`}
                            disabled={hiddenCriterionRows[item.id]}
                          />
                          <InlinePhotoAttach
                            count={criterionPhotos[item.id]?.length ?? 0}
                            expanded={Boolean(expandedCriterionPhotoPanels[item.id])}
                            disabled={hiddenCriterionRows[item.id]}
                            onToggle={() => setExpandedCriterionPhotoPanels(prev => ({
                              ...prev,
                              [item.id]: !prev[item.id],
                            }))}
                          />
                        </div>
                        {previewCommentField === `criterion:${item.id}`
                          && activeCommentField === `criterion:${item.id}`
                          && hasCommentFormattingSyntax(comments[item.id] ?? '')
                          && !hiddenCriterionRows[item.id] && (
                          <div className="mt-2 px-1">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                              Предпросмотр в отчёте
                            </div>
                            <div className="mt-1 text-sm leading-6 text-slate-700">
                              {renderFormattedComment(comments[item.id] ?? '')}
                            </div>
                          </div>
                        )}
                        {(criterionPhotos[item.id]?.length ?? 0) > 0 && (
                          <div className="mt-2">
                            <PhotoTiles
                              photos={criterionPhotos[item.id] ?? []}
                              emptyMessage="Фото не добавлены."
                              compact
                              onPreview={photo => setPreviewPhoto(photo)}
                              onRemove={photo => void removePhoto(photo, { kind: 'criterion', criterionCode: item.id })}
                            />
                          </div>
                        )}
                        {!hiddenCriterionRows[item.id] && expandedCriterionPhotoPanels[item.id] && (
                          <div
                            tabIndex={0}
                            onPaste={event => void pastePhotos(event, { kind: 'criterion', criterionCode: item.id })}
                            className="absolute right-0 top-[calc(100%+0.5rem)] z-20 w-[320px] max-w-[min(320px,calc(100vw-3rem))] rounded-xl border border-border bg-card p-3 shadow-lg focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                                onClick={() => {
                                  setPendingPhotoTarget({ kind: 'criterion', criterionCode: item.id })
                                  criterionPhotoInputRef.current?.click()
                                }}
                              >
                                <Paperclip className="h-3.5 w-3.5" />
                                Файл
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-xs font-medium transition-colors hover:border-primary/35 hover:bg-primary/5"
                                onClick={() => void addPhotosFromClipboard({ kind: 'criterion', criterionCode: item.id })}
                              >
                                <Clipboard className="h-3.5 w-3.5 text-primary" />
                                Из буфера
                              </button>
                            </div>
                            <div className="mt-2 text-[11px] text-muted-foreground">
                              Можно вставить изображение из буфера обмена или выбрать файл.
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <Card className="border border-border shadow-sm">
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">Общий комментарий</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Комментарий ко всему отсмотру целиком.
            </p>
          </div>
          {activeCommentField === 'general' && (
            <div className="flex items-center justify-end">
              <div className="flex items-center gap-2">
                {hasCommentFormattingSyntax(generalComment) && (
                  <button
                    type="button"
                    className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => setPreviewCommentField(prev => prev === 'general' ? null : 'general')}
                  >
                    {previewCommentField === 'general' ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                  </button>
                )}
                <CommentFormatToolbar
                  onFormat={definition => applyFormatToTextarea(
                    generalCommentRef.current,
                    generalComment,
                    updateGeneralComment,
                    definition
                  )}
                />
              </div>
            </div>
          )}
          <textarea
            ref={element => {
              generalCommentRef.current = element
              resizeCommentTextarea(element)
            }}
            value={generalComment}
            onChange={event => {
              updateGeneralComment(event.target.value)
              resizeCommentTextarea(event.currentTarget)
            }}
            onFocus={focusGeneralCommentField}
            onBlur={event => {
              if (event.relatedTarget instanceof Node && event.currentTarget.parentElement?.contains(event.relatedTarget)) {
                return
              }
              setActiveCommentField(prev => (prev === 'general' ? null : prev))
              setPreviewCommentField(prev => (prev === 'general' ? null : prev))
            }}
            className="input min-h-[120px] max-h-80 w-full resize-none overflow-y-auto text-sm"
            placeholder="Напишите общий комментарий"
          />
          {previewCommentField === 'general' && activeCommentField === 'general' && hasCommentFormattingSyntax(generalComment) && (
            <div className="px-1">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Предпросмотр в отчёте
              </div>
              <div className="mt-1 text-sm leading-6 text-slate-700">
                {renderFormattedComment(generalComment)}
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card className="border border-border shadow-sm">
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">Общие фото</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Нужно минимум {MIN_REVIEW_PHOTOS} фото на весь отсмотр.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Можно выбрать файл с устройства или вставить изображение из буфера обмена.
            </p>
          </div>

          <input
            ref={generalPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={event => {
              void addPhotos(event.target.files, { kind: 'general' })
              event.target.value = ''
            }}
          />

          <input
            ref={criterionPhotoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={event => {
              void addPhotos(event.target.files, pendingPhotoTarget)
              event.target.value = ''
            }}
          />

          <div
            ref={photosSectionRef}
            tabIndex={0}
            onPaste={event => void pastePhotos(event, { kind: 'general' })}
            className="space-y-4 rounded-lg border border-dashed border-border p-4 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
                onClick={() => generalPhotoInputRef.current?.click()}
              >
                <ImagePlus className="h-4 w-4" />
                Из файлов
              </button>
              <button
                type="button"
                className="inline-flex w-fit items-center gap-1.5 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/35 hover:bg-primary/5"
                onClick={() => void addPhotosFromClipboard({ kind: 'general' })}
              >
                <Clipboard className="h-4 w-4 text-primary" />
                Из буфера
              </button>
            </div>
            <div className="text-xs text-muted-foreground">
              Можно вставить изображение из буфера обмена или прикрепить файл с устройства.
            </div>

            <PhotoTiles
              photos={generalPhotos}
              emptyMessage="Фото пока не добавлены."
              compact
              onPreview={photo => setPreviewPhoto(photo)}
              onRemove={photo => void removePhoto(photo, { kind: 'general' })}
            />
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        <Card className="border border-border bg-card shadow-card">
          <div className="space-y-3">
            {(error || saveWarning || saveStatus) && (
              <div className="space-y-3">
                <div
                  role={error ? 'alert' : 'status'}
                  aria-live={error ? 'assertive' : 'polite'}
                  className={`rounded-lg border px-4 py-3 text-sm ${
                    error
                      ? 'border-error/30 bg-error/5 text-error'
                      : saveWarning
                        ? 'border-amber-300 bg-amber-50 text-amber-900'
                        : 'border-success/30 bg-success/5 text-success'
                  }`}
                >
                  {error || saveWarning || saveStatus}
                </div>

                {!error && activeRecordKey && !hasUnsavedReviewChanges && (
                  <Button
                    type="button"
                    size="md"
                    variant="outline"
                    className="w-full"
                    onClick={() => openReport(activeRecordKey)}
                    disabled={saving}
                  >
                    <Download className="mr-1.5 inline h-4 w-4" />
                    Скачать отчёт в PDF
                  </Button>
                )}
              </div>
            )}

            <Button
              type="button"
              size="md"
              variant="primary"
              className="w-full"
              onClick={() => void saveReview()}
              disabled={saving}
            >
              <Save className="mr-1.5 inline h-4 w-4" />
              {saving ? 'Сохраняем...' : 'Сохранить'}
            </Button>
          </div>
        </Card>
      </div>
        </>
      )}


      <PhotoPreviewOverlay
        photo={previewPhoto}
        onClose={() => setPreviewPhoto(null)}
      />
    </div>
  )
}
